import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import ReactQuill, { Quill } from 'react-quill'
import { debounce } from 'lodash'
import { v4 as uuidv4 } from 'uuid';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react'

// import mention from "quill-mention";
import 'react-quill/dist/quill.snow.css'
import 'quill-mention/dist/quill.mention.css'
import 'quill-mention'
import ImageModal from '../components/Comments/ImageModal'
import TableInsertModal from './TableInsertModal'
import './TableEmbed'
import { trimBlankEdges } from './pasteHtml'
import TableEditControls from './TableEditControls'
import ChecklistAssigneeDropdown, { ChecklistAssignee as ChecklistAssigneeDropdownType, ChecklistBoardMember } from './ChecklistAssigneeDropdown'
import ChecklistDueDatePicker from './ChecklistDueDatePicker'
import ChecklistCreateTaskModal from './ChecklistCreateTaskModal'
import {
  getLastChecklistColumn,
  setLastChecklistColumn,
} from '../utils/checklistColumnMemory'
import {
  CHECKLIST_COLUMN_DEFAULT_NAME,
  CHECKLIST_COLUMN_DEFAULT_COLOR,
} from '../hooks/useCreateChecklistColumn'
import { calculateUrgency, URGENCY_GREEN, URGENCY_AMBER, URGENCY_RED } from '../utils/urgencyColors'
import { sendPushAlert } from '../services/notification'
import AddNewContactModal from './Modals/AddNewContactModal'
import { useManageContacts } from '../hooks'
import { useTaskAssigneeSync } from '../hooks/useTaskAssigneeSync'
import { useTaskDueDateSync } from '../hooks/useTaskDueDateSync'
import { useUpgradeFreeTextAssignee } from '../hooks/useUpgradeFreeTextAssignee'
import { useSaveCardDescription } from '../hooks/useSaveCardDescription'



import { useSelector } from 'react-redux'
import { getAllPagesByBoardId } from '../services/pages'
import { useEditModal } from '../context/ShowEditModalQuill'
import ReactToolTip from './ReactToolTip'

import 'react-quill/dist/quill.snow.css'
import 'react-quill/dist/quill.snow.css'
import 'react-tooltip/dist/react-tooltip.css'
import '../styles/richTextStyle.css'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../modules/auth'
import { highlightPickerColors } from '../utils/quillColors'
import { extractMentionText } from '../utils/extractMentionText'
import { createSafeHtml } from '../utils/htmlSanitizer'
import { capitalizeEachWord } from '../helper-functions/CapitalizeName'

const Size = Quill.import('formats/size')
Size.whitelist = ['extra-small', 'small', 'medium', 'large']
Quill.register(Size, true)

const Font = Quill.import('formats/font')
Font.whitelist = ['arial', 'comic-sans', 'courier-new', 'georgia', 'helvetica', 'lucida']
Quill.register(Font, true)

const Link = Quill.import('formats/link')
Link.sanitize = function (url: any) {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`
  }
  return url;
}

// Snapshot the scroll position of every scrollable ancestor of `root`.
// Used to preserve the editor's scroll across a toolbar format action: the
// snapshot is taken on the toolbar mousedown (BEFORE Quill's own change handler
// calls quill.focus(), which re-focuses the .ql-editor element and makes the
// browser scroll it to the top), then restored after the format applies.
type ScrollPin = { el: HTMLElement; top: number }
function captureScrollPins(root: HTMLElement): ScrollPin[] {
  const pinned: ScrollPin[] = []
  for (let n: HTMLElement | null = root; n; n = n.parentElement) {
    const oy = window.getComputedStyle(n).overflowY
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) {
      pinned.push({ el: n, top: n.scrollTop })
    }
  }
  return pinned
}
// Restore captured positions across a short window — Quill's block swap and
// react-quill's async reconcile each reset scrollTop a frame or two later, so a
// one-shot restore loses the race.
function restoreScrollPins(pinned: ScrollPin[]): void {
  let frames = 0
  const restore = () => {
    pinned.forEach((p) => { p.el.scrollTop = p.top })
    if (++frames < 8) requestAnimationFrame(restore)
  }
  restore()
}

// BlockEmbed base for the checklist widget below. TableEmbed is registered in
// `./TableEmbed` via the side-effect import at the top of this file.
const BlockEmbed = Quill.import('blots/block/embed')

// ============================================================================
// Checklist embed — a self-contained checklist widget (heading, status bar,
// items with checkbox + text + assignee + per-item trash). Like TableEmbed,
// the wrapper is contenteditable=false and everything interactive inside it
// bypasses Quill. All event wiring lives in attachChecklistHandlers() so we
// can reattach on remount / paste.
// ============================================================================
class ChecklistEmbed extends BlockEmbed {
  static blotName = 'checklistEmbed'
  static tagName = 'DIV'
  static className = 'ql-checklist-embed'

  static create(value: string) {
    const node = super.create() as HTMLElement
    node.innerHTML = value
    node.setAttribute('contenteditable', 'false')
    node.setAttribute('data-checklist', 'true')

    attachChecklistHandlers(node)

    // Stop Quill from seeing copy/cut/paste inside the widget
    const stopQuill = (e: Event) => e.stopPropagation()
    node.addEventListener('copy', stopQuill, true)
    node.addEventListener('cut', stopQuill, true)
    node.addEventListener('paste', stopQuill, true)

    return node
  }

  static value(node: HTMLElement) {
    // Clone and strip transient UI (hover menus, focus outlines)
    const clone = node.cloneNode(true) as HTMLElement
    clone.querySelectorAll('.ql-checklist-assignee-dropdown').forEach((el) => el.remove())
    return clone.innerHTML
  }

  // Block Quill from deleting it — the heading trash icon is the only way
  deleteAt() { return }
  length() { return 1 }
}

Quill.register(ChecklistEmbed, true)

// The widget markup below is written multi-line for readability, but Quill's
// editor renders with `white-space: pre-wrap`, so every newline + indentation
// *between* tags becomes a real blank line inside the freshly-inserted widget
// (e.g. above the heading and between the heading and the first item). Quill's
// clipboard strips that inter-tag whitespace on the next convert (save →
// reopen), which is why the gap silently disappears after a reload. Collapse it
// here so the inserted markup matches the post-reopen form exactly.
const compactChecklistHtml = (html: string): string =>
  html.replace(/>\s+</g, '><').trim()

// Build the HTML for an empty checklist with one item
function buildEmptyChecklistHtml(): string {
  const id = `cl_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
  return compactChecklistHtml(`
    <div class="ql-checklist-heading" data-checklist-id="${id}">
      <span class="ql-checklist-heading-prefix">Checklist </span><span
        class="ql-checklist-heading-title"
        contenteditable="true"
        spellcheck="true"
        data-checklist-title="true"
        data-placeholder="title…"
      ></span>
      <div class="ql-checklist-statusbar" contenteditable="false">
        <div class="ql-checklist-statusbar-fill" style="width: 0%"></div>
        <span class="ql-checklist-statusbar-label">0 / 1</span>
      </div>
      <button
        type="button"
        class="ql-checklist-heading-trash"
        contenteditable="false"
        title="Delete checklist"
        aria-label="Delete checklist"
      ><i class="bi bi-trash-fill"></i></button>
    </div>
    <div class="ql-checklist-items">
      ${buildEmptyItemHtml()}
    </div>
  `)
}

function buildEmptyItemHtml(): string {
  const itemId = `cli_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
  return compactChecklistHtml(`
    <div class="ql-checklist-item" data-checklist-item="true" data-checklist-item-id="${itemId}" data-checklist-checked="false" draggable="true">
      <span
        class="ql-checklist-drag-handle"
        contenteditable="false"
        title="Drag to reorder"
        aria-label="Drag to reorder"
      >&#8942;&#8942;</span>
      <span
        class="ql-checklist-checkbox"
        contenteditable="false"
        role="checkbox"
        aria-label="Toggle checklist item"
      ></span>
      <span
        class="ql-checklist-item-text"
        contenteditable="true"
        spellcheck="true"
        data-placeholder="Next checklist item"
      ></span>
      <button
        type="button"
        class="ql-checklist-item-assignee ql-checklist-item-action"
        contenteditable="false"
        title="Assign to"
        aria-label="Assign to"
      ><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/></svg></button>
      <button
        type="button"
        class="ql-checklist-item-reminder ql-checklist-item-action"
        contenteditable="false"
        title="Set reminder"
        aria-label="Set reminder"
      ><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="-2 -2 22 22" fill="currentColor" stroke="currentColor" stroke-width="0.8" aria-hidden="true"><path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/><path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/></svg></button>
      <button
        type="button"
        class="ql-checklist-item-task ql-checklist-item-action"
        contenteditable="false"
        title="Create task from this item"
        aria-label="Create task from this item"
      >T</button>
      <button
        type="button"
        class="ql-checklist-item-trash ql-checklist-item-action"
        contenteditable="false"
        title="Delete item"
        aria-label="Delete item"
      ><i class="bi bi-trash-fill"></i></button>
    </div>
  `)
}

// Assignees stored on a checklist item. The object shape keeps one row per
// assigned entity regardless of source so the UI can render a union list.
//  - board: a member of the current board (has a valid b4igo userId, gets a push alert)
//  - contact: a saved contact of the current user (may or may not have an email)
//  - freetext: a name typed directly into the picker that didn't match a board
//              member or contact (ad-hoc, nothing is sent)
type ChecklistAssigneeSource = 'board' | 'contact' | 'freetext'
interface ChecklistAssignee {
  id: string
  name: string
  source: ChecklistAssigneeSource
  email?: string
  // Only set when source === 'contact' and the backend has confirmed the
  // contact email is associated with an active b4igo license — used to
  // decide whether to send an email notification.
  hasLicense?: boolean
  // Board-source assignees carry the board_person_id stamped at pick time
  // so create-task-from-checklist can build card_persons rows without
  // re-resolving userId → bpid against the board roster.
  _bpid?: string
}

// Session-level cache of checklist-item state keyed by item id. The live DOM
// data-attrs are the canonical persistence layer (they get saved with the
// description HTML), but the round-trip through Quill's clipboard.convert
// reliably strips arbitrary data-* attributes off inner item divs — so once
// `setEditor(quill.root.innerHTML)` fires after a write, the next read off
// the live DOM comes back empty even though the write succeeded. Mirroring
// every write here means dropdown re-opens and the create-task modal see
// the data the user actually entered. The cache is cleared on page reload
// (just a module-level Map); the data-attrs that DO survive cover that.
interface ChecklistItemCacheEntry {
  assignees?: ChecklistAssignee[]
  due_date?: string | null
  due_time?: string | null
  // Task-link attrs written after Create Task succeeds. Same round-trip
  // stripping that ate the assignees would otherwise leave the T icon
  // stuck on its unlinked colour and the click handler routed back to
  // the Create modal instead of the linked-task preview.
  task_id?: string | null
  task_key?: string | null
  task_status?: string | null
  // JSON-stringified payload of {board_person_id, fullName, userId}
  // triples — matches the data-checklist-task-bpids shape on disk.
  task_bpids?: string | null
}
const checklistItemCache = new Map<string, ChecklistItemCacheEntry>()

function cacheKeyFor(item: HTMLElement): string | null {
  return item.getAttribute('data-checklist-item-id')
}

function updateChecklistItemCache(
  item: HTMLElement,
  patch: Partial<ChecklistItemCacheEntry>,
): void {
  const key = cacheKeyFor(item)
  if (!key) return
  const prev = checklistItemCache.get(key) ?? {}
  checklistItemCache.set(key, { ...prev, ...patch })
}

// Read a checklist task-link attr (id / key / status / bpids) preferring
// the session cache and falling back to the live DOM attr. Seeds the
// cache from the DOM when the cache is empty so a subsequent rebuild
// that strips the attr off the live DOM still resolves correctly.
function readChecklistTaskAttr(
  item: HTMLElement,
  cacheField: 'task_id' | 'task_key' | 'task_status' | 'task_bpids',
  domAttr: 'data-checklist-task-id' | 'data-checklist-task-key' | 'data-checklist-task-status' | 'data-checklist-task-bpids',
): string | null {
  const key = cacheKeyFor(item)
  if (key) {
    const cached = checklistItemCache.get(key)
    if (cached && cached[cacheField] !== undefined) {
      return cached[cacheField] ?? null
    }
  }
  const value = item.getAttribute(domAttr)
  if (key && value != null) {
    updateChecklistItemCache(item, { [cacheField]: value })
  }
  return value
}

// Mirror a write of a task-link attr into the cache. Pass null to clear.
function writeChecklistTaskAttr(
  item: HTMLElement,
  cacheField: 'task_id' | 'task_key' | 'task_status' | 'task_bpids',
  domAttr: 'data-checklist-task-id' | 'data-checklist-task-key' | 'data-checklist-task-status' | 'data-checklist-task-bpids',
  value: string | null,
): void {
  if (value == null) {
    item.removeAttribute(domAttr)
  } else {
    item.setAttribute(domAttr, value)
  }
  updateChecklistItemCache(item, { [cacheField]: value })
}

export function readChecklistAssignees(item: HTMLElement): ChecklistAssignee[] {
  // Prefer the session cache when present — survives a Quill rebuild that
  // would otherwise strip data-checklist-assignees off the inner item div.
  const key = cacheKeyFor(item)
  if (key) {
    const cached = checklistItemCache.get(key)
    if (cached?.assignees) return cached.assignees
  }
  const raw = item.getAttribute('data-checklist-assignees')
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (Array.isArray(parsed)) {
      // Seed the cache from the DOM so subsequent reads (after a potential
      // rebuild that strips the attr) still find the data.
      if (key) updateChecklistItemCache(item, { assignees: parsed as ChecklistAssignee[] })
      return parsed as ChecklistAssignee[]
    }
  } catch {
    // fall through
  }
  return []
}

export function writeChecklistAssignees(item: HTMLElement, assignees: ChecklistAssignee[]): void {
  if (assignees.length === 0) {
    item.removeAttribute('data-checklist-assignees')
  } else {
    item.setAttribute('data-checklist-assignees', JSON.stringify(assignees))
  }
  updateChecklistItemCache(item, { assignees: assignees.length === 0 ? [] : assignees })
  const btn = item.querySelector<HTMLElement>('.ql-checklist-item-assignee')
  if (btn) {
    if (assignees.length > 0) {
      btn.setAttribute('data-has-assignees', 'true')
      btn.setAttribute('title', `Assigned to: ${assignees.map((a) => a.name).join(', ')}`)
    } else {
      btn.removeAttribute('data-has-assignees')
      btn.setAttribute('title', 'Assign to')
    }
  }
}

// Read/write the per-item due date+time. Stored as data-attrs so the value
// survives Quill blot rebuilds (the `static value()` cloner preserves them).
// Date is YYYY-MM-DD UTC, time is HH:mm:ss UTC — same shape the card uses
// (see EditBoardCard's getTimeDateFormate). Stored in UTC so syncing to a
// linked task is a direct field copy rather than re-conversion.
export function readChecklistDueDate(item: HTMLElement): Date | null {
  const { due_date, due_time } = readChecklistDueDateRaw(item)
  if (!due_date) return null
  const parsed = new Date(`${due_date}T${due_time ?? '00:00:00'}Z`)
  return isNaN(parsed.getTime()) ? null : parsed
}

export function writeChecklistDueDate(item: HTMLElement, dateTime: Date | null): void {
  if (!dateTime) {
    item.removeAttribute('data-checklist-due-date')
    item.removeAttribute('data-checklist-due-time')
    updateChecklistItemCache(item, { due_date: null, due_time: null })
  } else {
    const iso = dateTime.toISOString() // YYYY-MM-DDTHH:mm:ss.sssZ
    const date = iso.split('T')[0]
    const time = iso.split('T')[1].substring(0, 8)
    item.setAttribute('data-checklist-due-date', date)
    item.setAttribute('data-checklist-due-time', time)
    updateChecklistItemCache(item, { due_date: date, due_time: time })
  }
  applyChecklistDueColor(item)
}

// Read the raw UTC strings that match the card API shape (`due_date`,
// `due_time`). Returns null for either field when not set. Prefers the
// session cache (which survives Quill rebuilds that strip arbitrary
// data-* attrs); falls back to the DOM attrs and seeds the cache from
// them so future reads find the data even after a rebuild.
export function readChecklistDueDateRaw(item: HTMLElement): { due_date: string | null; due_time: string | null } {
  const key = cacheKeyFor(item)
  if (key) {
    const cached = checklistItemCache.get(key)
    if (cached && (cached.due_date !== undefined || cached.due_time !== undefined)) {
      return {
        due_date: cached.due_date ?? null,
        due_time: cached.due_time ?? null,
      }
    }
  }
  const due_date = item.getAttribute('data-checklist-due-date')
  const due_time = item.getAttribute('data-checklist-due-time')
  if (key && (due_date || due_time)) {
    updateChecklistItemCache(item, { due_date, due_time })
  }
  return { due_date, due_time }
}

// Paint the reminder button's background based on urgency.
//   no due date  → reset (CSS grey default takes over)
//   set          → green / amber / red bright via calculateUrgency
function dueColorFor(due: Date): string {
  const urgency = calculateUrgency(due)
  if (urgency <= 33) return URGENCY_GREEN
  if (urgency <= 67) return URGENCY_AMBER
  return URGENCY_RED
}

export function applyChecklistDueColor(item: HTMLElement): void {
  const btn = item.querySelector<HTMLElement>('.ql-checklist-item-reminder')
  if (!btn) return
  const due = readChecklistDueDate(item)
  if (!due) {
    btn.style.background = ''
    btn.style.color = ''
    btn.removeAttribute('data-has-due-date')
    btn.setAttribute('title', 'Set due date and time')
    return
  }
  const color = dueColorFor(due)
  btn.style.background = color
  btn.style.color = '#ffffff'
  btn.setAttribute('data-has-due-date', 'true')
  btn.setAttribute('title', `Due ${due.toLocaleString()}`)
}

// Minimum trimmed text length before an item's action icons are revealed.
// The trash icon shows as soon as there's any text so a mistaken row can be
// cleared immediately; the richer actions (assignee / reminder / task) wait
// until the item has enough text to be worth acting on. Gated in CSS via
// [data-checklist-has-text] (trash) and [data-checklist-has-actions] (rest).
const CHECKLIST_MIN_TRASH_CHARS = 1
const CHECKLIST_MIN_ACTION_CHARS = 3

// Timestamp of the last checkbox toggle. Toggling a checklist item mutates the
// editor DOM, which round-trips through react-quill's controlled setContents and
// makes keepCaretVisible follow the (stale) caret — yanking the view to wherever
// the user last typed. A toggle is not a text edit, so keepCaretVisible checks
// this and keeps the view still instead of chasing the caret.
let lastChecklistToggleAt = 0

// Update the status bar for one checklist node. Only items with non-empty
// text count toward the total — a trailing empty item acts as an "add next"
// placeholder and shouldn't bump the denominator.
function updateChecklistStatus(root: HTMLElement): void {
  const items = root.querySelectorAll<HTMLElement>('.ql-checklist-item')
  const filled = Array.from(items).filter((i) => {
    const text = i.querySelector<HTMLElement>('.ql-checklist-item-text')
    const len = (text?.textContent ?? '').trim().length
    // Gate the per-item action icons on text length: the trash icon after the
    // first character, the other three after CHECKLIST_MIN_ACTION_CHARS.
    if (len >= CHECKLIST_MIN_TRASH_CHARS) {
      i.setAttribute('data-checklist-has-text', 'true')
    } else {
      i.removeAttribute('data-checklist-has-text')
    }
    if (len >= CHECKLIST_MIN_ACTION_CHARS) {
      i.setAttribute('data-checklist-has-actions', 'true')
    } else {
      i.removeAttribute('data-checklist-has-actions')
    }
    return len > 0
  })
  const total = Math.max(filled.length, 1)
  const done = filled.filter(
    (i) => i.getAttribute('data-checklist-checked') === 'true'
  ).length
  const bar = root.querySelector<HTMLElement>('.ql-checklist-statusbar-fill')
  const label = root.querySelector<HTMLElement>('.ql-checklist-statusbar-label')
  const pct = Math.round((done / total) * 100)
  if (bar) {
    bar.style.width = `${pct}%`
    // Color shifts from red → amber → green as progress increases
    const hue = Math.round((done / total) * 120)
    bar.style.background = `hsl(${hue}, 70%, 50%)`
  }
  if (label) label.textContent = `${done} / ${total}`
}

// Remove a single checklist item from its node. If it's the last remaining
// item we clear it in place instead of removing (a checklist always keeps at
// least one line). Shared by the inline trash handler and the React-layer
// confirm flow so both paths behave identically.
function removeChecklistItem(node: HTMLElement, item: HTMLElement): void {
  const items = node.querySelectorAll('.ql-checklist-item')
  if (items.length <= 1) {
    const text = item.querySelector<HTMLElement>('.ql-checklist-item-text')
    if (text) text.textContent = ''
    item.setAttribute('data-checklist-checked', 'false')
  } else {
    item.remove()
  }
  updateChecklistStatus(node)
}

// Attach all the interactive handlers to a checklist node. Idempotent — safe
// to call multiple times; uses a WeakSet marker to skip already-wired nodes.
const checklistWiredNodes = new WeakSet<HTMLElement>()
function attachChecklistHandlers(node: HTMLElement): void {
  if (checklistWiredNodes.has(node)) return
  checklistWiredNodes.add(node)

  // Enforce 50-char max on heading
  const heading = node.querySelector<HTMLElement>('.ql-checklist-heading-title')
  if (heading) {
    heading.addEventListener('input', () => {
      const text = heading.textContent ?? ''
      if (text.length > 50) {
        heading.textContent = text.slice(0, 50)
        // Move caret to end
        const sel = window.getSelection()
        const range = document.createRange()
        range.selectNodeContents(heading)
        range.collapse(false)
        sel?.removeAllRanges()
        sel?.addRange(range)
      }
    })
    heading.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        // Move focus to the first item's text input
        const firstItemText = node.querySelector<HTMLElement>('.ql-checklist-item-text')
        firstItemText?.focus()
      }
    })
  }

  // Checkbox toggle — click anywhere in the checkbox span
  node.addEventListener('click', (e: Event) => {
    const target = e.target as HTMLElement
    const checkbox = target.closest?.('.ql-checklist-checkbox') as HTMLElement | null
    if (checkbox) {
      e.preventDefault()
      e.stopPropagation()
      const item = checkbox.closest<HTMLElement>('.ql-checklist-item')
      if (!item) return
      const checked = item.getAttribute('data-checklist-checked') === 'true'
      item.setAttribute('data-checklist-checked', checked ? 'false' : 'true')
      lastChecklistToggleAt = Date.now()
      updateChecklistStatus(node)
      return
    }

    // Per-item trash
    const itemTrash = target.closest?.('.ql-checklist-item-trash') as HTMLElement | null
    if (itemTrash) {
      e.preventDefault()
      e.stopPropagation()
      const item = itemTrash.closest<HTMLElement>('.ql-checklist-item')
      if (!item) return
      // If this item has a task created from it, defer to a confirmation modal
      // in the React layer — the created task is NOT removed with the item, so
      // we warn the user they'll need to find and delete it themselves.
      const taskId = readChecklistTaskAttr(item, 'task_id', 'data-checklist-task-id')
      const taskKey = readChecklistTaskAttr(item, 'task_key', 'data-checklist-task-key')
      if (taskId && taskKey) {
        if (!item.getAttribute('data-checklist-item-id')) {
          item.setAttribute(
            'data-checklist-item-id',
            `cli_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
          )
        }
        node.dispatchEvent(
          new CustomEvent('checklist-item-delete-request', {
            bubbles: true,
            detail: {
              itemId: item.getAttribute('data-checklist-item-id') as string,
              taskKey,
            },
          })
        )
        return
      }
      removeChecklistItem(node, item)
      return
    }

    // Reminder/clock icon — bubble an event; the React layer opens the
    // due-date picker popover. Same anchor-rect pattern as the assignee picker.
    const reminderBtn = target.closest?.('.ql-checklist-item-reminder') as HTMLElement | null
    if (reminderBtn) {
      e.preventDefault()
      e.stopPropagation()
      const item = reminderBtn.closest<HTMLElement>('.ql-checklist-item')
      if (!item) return
      const rect = reminderBtn.getBoundingClientRect()
      node.dispatchEvent(
        new CustomEvent('checklist-reminder-open', {
          bubbles: true,
          detail: { item, buttonRect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right } },
        })
      )
      return
    }

    // Assignee icon — bubble an event; the React layer will open the picker
    const assigneeBtn = target.closest?.('.ql-checklist-item-assignee') as HTMLElement | null
    if (assigneeBtn) {
      e.preventDefault()
      e.stopPropagation()
      const item = assigneeBtn.closest<HTMLElement>('.ql-checklist-item')
      if (!item) return
      const rect = assigneeBtn.getBoundingClientRect()
      node.dispatchEvent(
        new CustomEvent('checklist-assignee-open', {
          bubbles: true,
          detail: { item, buttonRect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right } },
        })
      )
      return
    }

    // Create-task "T" button — bubble an event; the React layer opens the
    // correct modal. If the item has already been converted to a task the
    // data-attrs will be set, and we show a preview instead of the creator.
    const taskBtn = target.closest?.('.ql-checklist-item-task') as HTMLElement | null
    if (taskBtn) {
      e.preventDefault()
      e.stopPropagation()
      const item = taskBtn.closest<HTMLElement>('.ql-checklist-item')
      if (!item) return
      // Stamp a stable id on the item so we can re-locate it after the
      // async create-task call. Redux refetches during the API call can
      // cause Quill to rebuild blots and detach our original reference.
      if (!item.getAttribute('data-checklist-item-id')) {
        item.setAttribute(
          'data-checklist-item-id',
          `cli_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`
        )
      }
      const itemId = item.getAttribute('data-checklist-item-id') as string
      const textEl = item.querySelector<HTMLElement>('.ql-checklist-item-text')
      const text = (textEl?.textContent ?? '').trim()
      // Cache-aware reads: after Create Task the live attrs get stripped
      // by Quill's clipboard.convert round-trip, so a direct getAttribute
      // would route every subsequent click into the create flow even
      // though the task already exists. Same workaround as for assignees.
      const taskId = readChecklistTaskAttr(item, 'task_id', 'data-checklist-task-id')
      const taskKey = readChecklistTaskAttr(item, 'task_key', 'data-checklist-task-key')
      if (taskId && taskKey) {
        node.dispatchEvent(
          new CustomEvent('checklist-task-preview', {
            bubbles: true,
            detail: { itemId, taskId, taskKey, title: text },
          })
        )
      } else {
        const embed = item.closest<HTMLElement>('.ql-checklist-embed')
        const heading = embed?.querySelector<HTMLElement>('.ql-checklist-heading-title')
        const checklistTitle = (heading?.textContent ?? '').trim() || 'Checklist'
        node.dispatchEvent(
          new CustomEvent('checklist-create-task', {
            bubbles: true,
            detail: { itemId, text, checklistTitle },
          })
        )
      }
      return
    }

    // Heading trash — let the outer TextEditor handler take care of confirm + removal
    const headingTrash = target.closest?.('.ql-checklist-heading-trash') as HTMLElement | null
    if (headingTrash) {
      e.preventDefault()
      e.stopPropagation()
      // Dispatch a custom event so the React layer can show the confirm modal
      node.dispatchEvent(new CustomEvent('checklist-delete-request', { bubbles: true }))
      return
    }
  }, true)

  // Live-update the counter when item text changes (total counts non-empty items)
  node.addEventListener('input', (e: Event) => {
    const target = e.target as HTMLElement
    if (target?.closest?.('.ql-checklist-item-text')) {
      updateChecklistStatus(node)
    }
  }, true)

  // Keyboard handling inside item text — Enter adds a new item, double-Enter exits
  node.addEventListener('keydown', (e: KeyboardEvent) => {
    const target = e.target as HTMLElement
    const itemText = target.closest?.('.ql-checklist-item-text') as HTMLElement | null
    if (!itemText) return
    const item = itemText.closest<HTMLElement>('.ql-checklist-item')
    if (!item) return

    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      const isEmpty = (itemText.textContent ?? '').trim().length === 0
      if (isEmpty) {
        // Double-Enter on an empty item exits the checklist.
        // Remove the empty trailing item and dispatch an event asking the
        // outer editor to move the cursor to the paragraph after the blot.
        const items = node.querySelectorAll<HTMLElement>('.ql-checklist-item')
        if (items.length > 1) {
          item.remove()
        } else {
          // Don't leave the checklist with zero items — keep one, but still exit
        }
        updateChecklistStatus(node)
        node.dispatchEvent(new CustomEvent('checklist-mutated', { bubbles: true }))
        node.dispatchEvent(new CustomEvent('checklist-exit', { bubbles: true }))
        return
      }
      // Non-empty: create a new item and focus its text
      const container = node.querySelector<HTMLElement>('.ql-checklist-items')
      if (!container) return
      const wrapper = document.createElement('div')
      wrapper.innerHTML = buildEmptyItemHtml().trim()
      const newItem = wrapper.firstElementChild as HTMLElement
      item.insertAdjacentElement('afterend', newItem)
      updateChecklistStatus(node)
      const newText = newItem.querySelector<HTMLElement>('.ql-checklist-item-text')
      newText?.focus()
      node.dispatchEvent(new CustomEvent('checklist-mutated', { bubbles: true }))
      return
    }

    if (e.key === 'Backspace') {
      const isEmpty = (itemText.textContent ?? '').length === 0
      const items = node.querySelectorAll<HTMLElement>('.ql-checklist-item')
      if (isEmpty && items.length > 1) {
        // Remove this empty item and focus the previous one
        e.preventDefault()
        const idx = Array.from(items).indexOf(item)
        const prev = idx > 0 ? items[idx - 1] : null
        item.remove()
        updateChecklistStatus(node)
        const prevText = prev?.querySelector<HTMLElement>('.ql-checklist-item-text')
        if (prevText) {
          prevText.focus()
          // Put caret at end
          const sel = window.getSelection()
          const range = document.createRange()
          range.selectNodeContents(prevText)
          range.collapse(false)
          sel?.removeAllRanges()
          sel?.addRange(range)
        }
        return
      }
    }
  }, true)

  // Drag-and-drop reorder. Each item is draggable; the drop target is the
  // item the cursor is over — we insert the dragged item before/after based
  // on mouse Y within that row. We enforce same-checklist scope by reading
  // the dragging element via a closure variable rather than dataTransfer.
  let draggingItem: HTMLElement | null = null

  node.addEventListener('dragstart', (e: DragEvent) => {
    const target = e.target as HTMLElement
    const item = target.closest?.('.ql-checklist-item') as HTMLElement | null
    if (!item) return
    // Only start a drag if the grip handle (or the item background) was grabbed,
    // not the text input or action buttons — those need their own click behavior.
    const onText = target.closest?.('.ql-checklist-item-text')
    const onAction = target.closest?.('.ql-checklist-item-action')
    if (onText || onAction) {
      e.preventDefault()
      return
    }
    draggingItem = item
    item.classList.add('ql-checklist-item-dragging')
    // Use the item's HTML as drag data so copy-to-another-checklist still works
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move'
      // Setting some data keeps Firefox happy
      e.dataTransfer.setData('text/plain', 'checklist-item')
    }
  }, true)

  node.addEventListener('dragover', (e: DragEvent) => {
    if (!draggingItem) return
    const target = e.target as HTMLElement
    const overItem = target.closest?.('.ql-checklist-item') as HTMLElement | null
    if (!overItem || overItem === draggingItem) return
    // Only allow reorder within the same checklist node
    if (!node.contains(overItem)) return
    e.preventDefault()
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move'
    const rect = overItem.getBoundingClientRect()
    const above = e.clientY < rect.top + rect.height / 2
    // Clear any previous indicator
    node.querySelectorAll('.ql-checklist-item-drop-above, .ql-checklist-item-drop-below').forEach((el) => {
      el.classList.remove('ql-checklist-item-drop-above', 'ql-checklist-item-drop-below')
    })
    overItem.classList.add(above ? 'ql-checklist-item-drop-above' : 'ql-checklist-item-drop-below')
  })

  node.addEventListener('drop', (e: DragEvent) => {
    if (!draggingItem) return
    const target = e.target as HTMLElement
    const overItem = target.closest?.('.ql-checklist-item') as HTMLElement | null
    if (!overItem || overItem === draggingItem || !node.contains(overItem)) {
      return
    }
    e.preventDefault()
    const rect = overItem.getBoundingClientRect()
    const above = e.clientY < rect.top + rect.height / 2
    if (above) overItem.before(draggingItem)
    else overItem.after(draggingItem)
    updateChecklistStatus(node)
  })

  node.addEventListener('dragend', () => {
    if (draggingItem) draggingItem.classList.remove('ql-checklist-item-dragging')
    draggingItem = null
    node.querySelectorAll('.ql-checklist-item-drop-above, .ql-checklist-item-drop-below').forEach((el) => {
      el.classList.remove('ql-checklist-item-drop-above', 'ql-checklist-item-drop-below')
    })
    // Persist the new order via a custom event so the React layer can
    // setEditor(quill.root.innerHTML) after the DOM mutation
    node.dispatchEvent(new CustomEvent('checklist-mutated', { bubbles: true }))
  })

  // Ensure any pre-existing items (e.g. loaded from saved HTML that predates
  // the drag handle) get draggable set and a handle prepended.
  node.querySelectorAll<HTMLElement>('.ql-checklist-item').forEach((item) => {
    if (item.getAttribute('draggable') !== 'true') {
      item.setAttribute('draggable', 'true')
    }
    if (!item.querySelector('.ql-checklist-drag-handle')) {
      const handle = document.createElement('span')
      handle.className = 'ql-checklist-drag-handle'
      handle.setAttribute('contenteditable', 'false')
      handle.setAttribute('title', 'Drag to reorder')
      handle.setAttribute('aria-label', 'Drag to reorder')
      handle.innerHTML = '&#8942;&#8942;'
      item.insertBefore(handle, item.firstChild)
    }
    // Upgrade legacy assignee button (emoji 👤) to monochrome SVG so CSS
    // `color` can paint it white against the purple background.
    const assigneeBtn = item.querySelector<HTMLElement>('.ql-checklist-item-assignee')
    if (assigneeBtn && !assigneeBtn.querySelector('svg')) {
      assigneeBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm2-3a2 2 0 1 1-4 0 2 2 0 0 1 4 0zm4 8c0 1-1 1-1 1H3s-1 0-1-1 1-4 6-4 6 3 6 4zm-1-.004c-.001-.246-.154-.986-.832-1.664C11.516 10.68 10.289 10 8 10c-2.29 0-3.516.68-4.168 1.332-.678.678-.83 1.418-.832 1.664h10z"/></svg>'
    }
    // Upgrade legacy reminder button (emoji 🕑) to monochrome SVG.
    const reminderBtn = item.querySelector<HTMLElement>('.ql-checklist-item-reminder')
    if (reminderBtn && !reminderBtn.querySelector('svg')) {
      reminderBtn.innerHTML =
        '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="-2 -3 20 20" fill="currentColor" stroke="currentColor" stroke-width="0.8" aria-hidden="true"><path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/><path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/></svg>'
    }
    // Upgrade legacy trash button (emoji 🗑) to Bootstrap Icons.
    const trashBtn = item.querySelector<HTMLElement>('.ql-checklist-item-trash')
    if (trashBtn && !trashBtn.querySelector('i.bi')) {
      trashBtn.innerHTML = '<i class="bi bi-trash-fill"></i>'
    }
    // Paint the reminder button color from any persisted due date
    applyChecklistDueColor(item)
  })

  // Upgrade legacy heading trash button (emoji 🗑) to Bootstrap Icons.
  const headingTrashBtn = node.querySelector<HTMLElement>('.ql-checklist-heading-trash')
  if (headingTrashBtn && !headingTrashBtn.querySelector('i.bi')) {
    headingTrashBtn.innerHTML = '<i class="bi bi-trash-fill"></i>'
  }

  // Recompute status on initial mount in case the HTML already had items
  updateChecklistStatus(node)
}

// Quill.register('modules/mention', mention, true);

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
interface TextEditorProps {
  setEditorTags?: React.Dispatch<React.SetStateAction<any | null>>
  setEditor: React.Dispatch<React.SetStateAction<string | null>>
  editor: string | null
  placeholder: string
  forTitle?: string
  tocHeading?: any
  setTocHeading?: (value: any) => void
  role?: string
  boardId?: string
  tabIndex?: any
  onFocus?: any
  handleFormSubmission?: () => void
  optionsForSendAlert?: any
  // Full-detail board members (with email) used by the checklist assignee
  // dropdown and for sending alerts to newly assigned members. Caller can
  // pass the `boardPersonsForSendAlert` array from useBoardPersons.
  boardPersonsForSendAlert?: any
  setDesc?: any
  setMentions?: any
  retainMentions?: any
  uniqueId?: any
  isHome?: boolean
  isNewModal?: boolean
  currentCardOrPage?: any
  isExpandedModal?: boolean
  // Gate the checklist widget (toolbar button, inline "+" trigger, assignee
  // picker, etc.) — for now only card/task-description editors opt in.
  enableChecklist?: boolean
  // Called when the user clicks the "T" button on a checklist item and
  // confirms the "create task" modal. The parent form owns card creation
  // because it has the board/column/websocket context. Should return the
  // new card's id + key (e.g. "GoAI-999") so the checklist item can be
  // linked to the created task.
  onCreateTaskFromChecklistItem?: (params: {
    title: string
    assignees: ChecklistAssignee[]
    due_date?: string | null
    due_time?: string | null
    columnId?: string | null
  }) => Promise<{
    id: string
    key: string
    assignees?: Array<{ board_person_id: string; fullName: string; userId?: string }>
  } | void>
  // Board columns (id/name/color/position) shown in the create-task modal's
  // Column dropdown. Optional — when absent the column picker is hidden and
  // the task inherits the parent card's column (legacy behaviour).
  columns?: any[]
  // Creates a board column on demand from the "+ New column" flow and
  // returns the persisted column. The parent owns this so it can broadcast
  // the columnCreate websocket and refresh its own column list.
  onCreateColumnFromChecklist?: (params: {
    name: string
    color: string
    columns: any[]
  }) => Promise<any>
}

const MemoizedTextEditor: React.FC<TextEditorProps> = ({
  setEditor,
  editor,
  placeholder,
  tocHeading,
  // forTitle,
  role,
  boardId,
  tabIndex,
  onFocus,
  optionsForSendAlert,
  boardPersonsForSendAlert,
  // setDesc,
  handleFormSubmission, setEditorTags = () => undefined,
  setMentions, retainMentions, isHome, isNewModal,
  uniqueId,
  currentCardOrPage,
  isExpandedModal,
  enableChecklist = false,
  onCreateTaskFromChecklistItem,
  columns,
  onCreateColumnFromChecklist,
}): any => {
  const { setShowEditModalQuill, setCard, pushToHistory, setExpandedFromSource } = useEditModal()
  // const { setShowEditPageModalQuill, setPage } = useEditPageModal()
  const editRef: any = useRef()
  const quillRef: any = useRef(null)
  // Scroll snapshot taken on toolbar mousedown (before Quill's focus() scrolls
  // the editor to the top), restored after a block-format toolbar action.
  const toolbarScrollSnapshotRef = useRef<ScrollPin[]>([])
  const isExpandedModalRef = useRef(isExpandedModal)
  const optionsForSendAlertRef = useRef(optionsForSendAlert)
  const { theme } = useTheme()
  const { currentUser } = useAuth()
  const { fullName } = currentUser?.data.user

  // Gate processHtml — only walk the DOM when something changed that needs re-coloring
  // (paste, theme change). Plain typing skips the walk entirely.
  const needsProcessHtmlRef = useRef(false)

  // Debounce mention extraction — DOMParser on big HTML was the main per-keystroke stall
  const debouncedExtractMentions = useMemo(
    () => debounce((content: string) => {
      if (setMentions) extractMentionText(content, setMentions, retainMentions)
    }, 200),
    [setMentions, retainMentions]
  )

  useEffect(() => {
    return () => debouncedExtractMentions.cancel()
  }, [debouncedExtractMentions])

  useEffect(() => {
    needsProcessHtmlRef.current = true
  }, [theme])

  useEffect(() => {
    if (quillRef.current && onFocus) {
      quillRef.current.focus();  // Focus editor when triggered
    }
  }, [onFocus]);

  useEffect(() => {
    isExpandedModalRef.current = isExpandedModal
  }, [isExpandedModal])

  const editorContainerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    optionsForSendAlertRef.current = optionsForSendAlert
  }, [optionsForSendAlert])

  // Fix ordered list numbering: continues across bullet lists, resets at blank lines
  useEffect(() => {
    const timer = setTimeout(() => {
      const editorEl = editorContainerRef.current?.querySelector('.ql-editor')
      if (!editorEl) return

      // Quick check: skip if no <ol> elements exist
      const olElements = editorEl.getElementsByTagName('ol')
      if (olElements.length === 0) return

      let runningCount = 0
      const children = editorEl.children

      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement
        const tag = child.tagName.toLowerCase()

        if (tag === 'ol') {
          child.style.counterReset = `list-0 ${runningCount} list-1 0 list-2 0 list-3 0 list-4 0 list-5 0 list-6 0 list-7 0 list-8 0 list-9 0`
          runningCount += child.querySelectorAll(':scope > li:not([class*="ql-indent"])').length
        } else if (tag !== 'ul') {
          runningCount = 0
        }
      }
    }, 150)
    return () => clearTimeout(timer)
  }, [editor])

  const [isVideoSrcGet, setIsVideoSrcGet] = useState(false)
  const [cardSelected, setCardSelected] = useState<any>(null)
  const [pageSelected, setPageSelected] = useState<any>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showHighlightPicker, setShowHighlightPicker] = useState(false)
  const [highlightColor, setHighlightColor] = useState('#ffff00')
  const [showTextColorPicker, setShowTextColorPicker] = useState(false)
  const [textColor, setTextColor] = useState('#000000')
  const emojiPickerRef = useRef<HTMLDivElement>(null)
  const highlightPickerRef = useRef<HTMLDivElement>(null)
  const colorInputRef = useRef<HTMLInputElement>(null)
  const textColorPickerRef = useRef<HTMLDivElement>(null)
  const textColorInputRef = useRef<HTMLInputElement>(null)
  const savedCursorPosition = useRef<any>(null)
  const tableCellSelectionRef = useRef<Range | null>(null)
  const [showTableModal, setShowTableModal] = useState(false)
  // --- Checklist state ---
  // Anchor for the inline "+" dropdown. Null when no dropdown is shown.
  const [checklistDropdown, setChecklistDropdown] = useState<{
    top: number
    left: number
    plusIndex: number
  } | null>(null)
  // Node pending deletion via the heading trash confirm modal
  const pendingChecklistDeleteRef = useRef<HTMLElement | null>(null)
  const [showChecklistDeleteConfirm, setShowChecklistDeleteConfirm] = useState(false)
  // --- Per-item delete confirm (only raised when the item has a task created
  // from it). Tracked by stable item id so we can re-locate the DOM node on
  // confirm even if Quill rebuilt the blot in between. ---
  const [itemDeleteConfirm, setItemDeleteConfirm] = useState<
    { itemId: string; taskKey: string } | null
  >(null)
  // --- Create-task-from-checklist modal state ---
  // Track the item by its stable string id (stamped onto the DOM when the T
  // button is clicked) so we can re-locate it after the async API call even
  // if Quill has rebuilt the blot during a redux-driven re-render.
  const [taskFromChecklist, setTaskFromChecklist] = useState<{
    itemId: string
    text: string
    checklistTitle: string
    assignees: ChecklistAssigneeDropdownType[]
    due_date: string | null
    due_time: string | null
  } | null>(null)
  const [taskFromChecklistSaving, setTaskFromChecklistSaving] = useState(false)
  const [taskFromChecklistError, setTaskFromChecklistError] = useState<string | null>(null)
  // Column choice for the create-task-from-checklist modal. `mode` toggles
  // between picking an existing column and the inline "+ New column" editor.
  const [columnChoice, setColumnChoice] = useState<{
    mode: 'select' | 'new'
    columnId: string
    newName: string
    newColor: string
  }>({
    mode: 'select',
    columnId: '',
    newName: CHECKLIST_COLUMN_DEFAULT_NAME,
    newColor: CHECKLIST_COLUMN_DEFAULT_COLOR,
  })
  // Tracks which checklist item's modal we've already seeded a default
  // column for, so a columns refetch while the modal is open doesn't stomp
  // the user's selection.
  const columnSeededItemRef = useRef<string | null>(null)
  // --- Task-preview modal state (shown when a checklist item is already linked) ---
  const [taskPreview, setTaskPreview] = useState<
    { taskId: string; taskKey: string; title: string } | null
  >(null)
  // --- Assignee picker state ---
  // Anchor + item for the currently open assignee dropdown. Null when closed.
  // Guard against the picker being re-opened immediately after closing.
  // Clicking the assignee button to close the picker generates both a
  // mousedown (triggers outside-click close) and a click (triggers open)
  // back-to-back — without this guard the second gesture reopens the
  // picker and produces a second stray PATCH on the next close.
  const lastAssigneeCloseRef = useRef<{ itemId: string; at: number } | null>(null)
  const [assigneePicker, setAssigneePicker] = useState<{
    anchorRect: { top: number; bottom: number; left: number; right: number }
    item: HTMLElement
    initial: ChecklistAssigneeDropdownType[]
  } | null>(null)
  // --- Due-date picker state ---
  // Opens when the user clicks a checklist item's clock icon.
  const [reminderPicker, setReminderPicker] = useState<{
    anchorRect: { top: number; bottom: number; left: number; right: number }
    item: HTMLElement
    initial: Date | null
  } | null>(null)
  // --- Create-new-contact modal state ---
  // Opens when the user clicks "+ Add <name> to contacts" in the assignee
  // dropdown. Tracked by stable checklist-item id so we can re-locate the
  // item after the contact is saved (re-renders may swap DOM references).
  const [createContactModal, setCreateContactModal] = useState<{
    name: string
    itemId: string
  } | null>(null)
  const [createContactFormValues, setCreateContactFormValues] = useState<any>({})
  const { getOtherContactHandler } = useManageContacts()
  // Always read the latest boardPersons inside event handlers
  const boardPersonsForSendAlertRef = useRef<any[]>(boardPersonsForSendAlert || [])
  useEffect(() => {
    boardPersonsForSendAlertRef.current = boardPersonsForSendAlert || []
  }, [boardPersonsForSendAlert])

  // const [cursorRange, setCursorRange] = useState({ min: null, max: null,start: null });
  // const { allCurrentUserCards } = useSelector((state: any) => state.cardReducer)
  const { pagesByLoggedInUser: pages } = useSelector((state: any) => state.pageReducer)
  const { allCurrentUserCards } = useSelector((state: any) => state.cardReducer)
  // Also pull the board-scoped cards slice — used to detect when a
  // linked task has been deleted vs simply not visible to the current
  // user (a task can be on the board without being assigned to me, so
  // allCurrentUserCards alone can't distinguish "deleted" from
  // "filtered out").
  const boardScopedCards = useSelector((state: any) => state.cardReducer.cards)
  // Ref mirrors the redux cards so event handlers registered once-on-mount
  // read the latest task data (assignees, status) without being stale.
  const allCurrentUserCardsRef = useRef<any[]>(allCurrentUserCards || [])
  useEffect(() => {
    allCurrentUserCardsRef.current = allCurrentUserCards || []
  }, [allCurrentUserCards])
  // Same ref pattern for the board-scoped slice — a checklist can link to
  // a task the current user isn't assigned to (so it never lands in
  // allCurrentUserCards), but it WILL be in boardScopedCards as long as
  // the board is loaded. Without this fallback, syncAssigneesToTask
  // silently bails for those tasks and the picker becomes a no-op.
  const boardScopedCardsRef = useRef<any[]>(boardScopedCards || [])
  useEffect(() => {
    boardScopedCardsRef.current = boardScopedCards || []
  }, [boardScopedCards])
  // The `modules` object (and the quill-mention `source` inside it) is memoized
  // once with `[]` deps, so its closure captures the FIRST-render values of
  // `pages`, `boardId` and `fullName`. By the time the user types `#`, those are
  // stale (the pages fetch has resolved, or the open page/board has changed),
  // which made the `#` page list come back empty. Read these through refs so the
  // once-captured `source` always sees live values.
  const pagesRef = useRef<any[]>(pages || [])
  useEffect(() => {
    pagesRef.current = pages || []
  }, [pages])
  // Board-scoped page list for the `#` mention, fetched on demand (only when the
  // user actually types `#`) and cached per board for this editor's lifetime.
  // We deliberately keep this OUT of redux: routing it through the page slice
  // re-rendered every TextEditor instance subscribed to that slice (description
  // + every comment in the task modal) each time the list updated.
  const boardPagesCacheRef = useRef<Record<string, any[]>>({})
  const boardIdRef = useRef<any>(boardId)
  useEffect(() => {
    boardIdRef.current = boardId
  }, [boardId])
  // The open card/page carries a boardId even when no `boardId` prop is passed
  // (e.g. the homepage page editor), so `#` mentions can resolve the board there too.
  const currentCardOrPageRef = useRef<any>(currentCardOrPage)
  useEffect(() => {
    currentCardOrPageRef.current = currentCardOrPage
  }, [currentCardOrPage])
  const fullNameRef = useRef<any>(fullName)
  useEffect(() => {
    fullNameRef.current = fullName
  }, [fullName])

  const handleMouseover = (id: string) => {
    const cardInfo: any = allCurrentUserCards?.find((card: any) => `${card?.key.trim()}` === id)
    const pageInfo: any = pages?.find((page: any) => `${page?.id}` === id)
    setShowEditModalQuill(false)
    // setShowEditPageModalQuill(false)
    if (cardInfo) {
      setCardSelected(cardInfo)
    }
    if (pageInfo) {
      setPageSelected(pageInfo)
    }
  }

  const debouncedOnChange = debounce(handleMouseover, 300)

  const handleCopy = (e: ClipboardEvent) => {
    // If the event target or active element is inside a table embed, let native copy handle it
    const target = e.target as HTMLElement;
    const active = document.activeElement as HTMLElement;
    if (target?.closest?.('.ql-table-embed') || active?.closest?.('.ql-table-embed')) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // Use browser's native selection to grab HTML — this preserves tables
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;

    const nativeRange = sel.getRangeAt(0);
    const fragment = nativeRange.cloneContents();
    const tempDiv = document.createElement('div');
    tempDiv.appendChild(fragment);

    // If the selection includes a table, use the native DOM HTML
    if (tempDiv.querySelector('table, .ql-table-embed')) {
      const html = tempDiv.innerHTML;
      const simpleText = tempDiv.innerText;
      const copyId = uuidv4();
      const wrappedHtml = `<div data-editor-source="${copyId}" data-editor-theme="${theme}" data-editor-app="your-app-name">${html}</div>`;
      e.clipboardData?.setData('text/html', wrappedHtml);
      e.clipboardData?.setData('text/plain', simpleText);
      e.preventDefault();
      return;
    }

    // No table — use Quill's delta-based copy for better formatting preservation
    const range = editor.getSelection();
    if (!range || range.length === 0) return;
    const selectedContent = editor.getContents(range.index, range.length);
    const tempQuillDiv = document.createElement('div');
    const tempQuill = new Quill(tempQuillDiv);
    tempQuill.setContents(selectedContent);
    let html = tempQuillDiv.querySelector('.ql-editor')?.innerHTML || '';
    const simpleText = (tempQuillDiv.querySelector('.ql-editor') as HTMLElement)?.innerText || '';
    const copyId = uuidv4();
    html = `<div data-editor-source="${copyId}" data-editor-theme="${theme}" data-editor-app="your-app-name">${html}</div>`;
    e.clipboardData?.setData('text/html', html);
    e.clipboardData?.setData('text/plain', simpleText);
    e.preventDefault();
  };

  // Helper: paste HTML that may contain tables — splits into chunks,
  // pastes non-table parts via Quill, inserts tables as tableEmbed blots
  const pasteHtmlWithTables = (editor: any, index: number, html: string) => {
    const container = document.createElement('div');
    container.innerHTML = html;
    const trimmed = trimBlankEdges(container);

    // Check if there are any tables
    const tables = container.querySelectorAll('table');
    if (tables.length === 0) {
      // No tables — use standard Quill paste
      editor.clipboard.dangerouslyPasteHTML(index, trimmed);
      return;
    }

    // Process nodes in order, splitting at table boundaries
    let currentIndex = index;
    const children = Array.from(container.childNodes);
    let nonTableBuffer = '';

    const flushBuffer = () => {
      if (nonTableBuffer.trim()) {
        editor.clipboard.dangerouslyPasteHTML(currentIndex, nonTableBuffer);
        currentIndex = editor.getLength() - 1;
        nonTableBuffer = '';
      }
    };

    children.forEach((child: any) => {
      if (child.nodeName === 'TABLE' || (child.querySelector && child.querySelector('table'))) {
        flushBuffer();
        // Extract just the table HTML
        const tableHtml = child.nodeName === 'TABLE'
          ? child.outerHTML
          : child.querySelector('table').outerHTML;
        editor.insertEmbed(currentIndex, 'tableEmbed', tableHtml, 'user');
        currentIndex = editor.getLength() - 1;
      } else {
        nonTableBuffer += child.outerHTML || child.textContent || '';
      }
    });
    flushBuffer();
  };

  const handlePaste = (e: ClipboardEvent) => {
    // If the event target or active element is inside a table or checklist embed,
    // let native paste handle it — those widgets manage their own contenteditable inputs.
    const target = e.target as HTMLElement;
    const active = document.activeElement as HTMLElement;
    if (target?.closest?.('.ql-table-embed') || active?.closest?.('.ql-table-embed')) return;
    if (target?.closest?.('.ql-checklist-embed') || active?.closest?.('.ql-checklist-embed')) return;
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    // Pasted content can carry foreign colors that need theme adaptation — let the
    // next handleChange run processHtml once.
    needsProcessHtmlRef.current = true
    const clipboardData = e.clipboardData || (window as any).clipboardData;
    if (!clipboardData) return;
    const files = clipboardData.files
    if (files && files.length > 0) {
      // Check if there are any image files
      const hasImageFiles = Array.from(files).some((file: any) => file.type.startsWith('image/'));

      if (hasImageFiles) {
        Array.from(files).forEach((file: any) => {
          if (file.type.startsWith('image/')) {
            // Handle image pasting
            const reader = new FileReader();
            reader.onload = (loadEvent) => {
              const range = editor.getSelection();
              if (range) {
                const imageUrl = loadEvent.target?.result as string;
                // Insert image at cursor position
                if (range?.length > 0) editor.deleteText(range.index, range.length);
                editor.insertEmbed(range.index, 'image', imageUrl);

                const newContent = editor.root.innerHTML;
                setEditor(newContent);
                // Move cursor after the image
                setTimeout(() => {
                  editor.setSelection(range.index + 1, 0);
                }, 50);
              }
            };
            reader.readAsDataURL(file);
          }
        });
        // CRITICAL: Return early to prevent HTML processing below
        return;
      }
    }
    const html = clipboardData?.getData('text/html');
    if (!html) {
      // Handle plain text pasting if no HTML
      editor.focus(); // Ensure the editor is active
      const text = clipboardData.getData('text/plain');
      if (text) {
        let range = editor.getSelection();

        // If no selection but editor has focus, get cursor position
        if (!range && editor.hasFocus()) {
          const cursorPosition = editor.getLength() - 1;
          range = { index: cursorPosition, length: 0 };
        }
        // If no selection and no focus, use start of document
        else if (!range) {
          range = { index: 0, length: 0 };
        }
        if (range) {
          // Insert the plain text
          if (range?.length > 0) editor.deleteText(range.index, range.length);
          editor.insertText(range.index, text);

          // Update editor content state
          const newContent = editor.root.innerHTML;
          setEditor(newContent);

          // Position cursor after the inserted text
          setTimeout(() => {
            const newCursorPosition = Math.min(
              range.index + text.length,
              // editor.getLength() - 1
            );
            editor.setSelection(newCursorPosition, 0);

            // Try to trigger mention dropdown for pasted task IDs
            // This will delete the pasted text and re-insert with keyboard simulation
            triggerMentionForPastedId(editor, text, range.index);
          }, 150);
        }
      }
      return;
    }
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    // Check if content is from our editor by looking for our data attribute
    const editorContent = tempDiv.querySelector('[data-editor-source]');
    if (editorContent) {
      // This is content from our editor - preserve ALL formatting
      const sourceTheme = editorContent.getAttribute('data-editor-theme');
      const contentToInsert = editorContent.innerHTML;
      // Determine if we need to adapt colors for theme changes
      const needsThemeAdaptation = sourceTheme !== theme;
      if (needsThemeAdaptation) {
        // Theme adaptation for content from our editor in different theme
        const elementsToAdapt = tempDiv.querySelectorAll('*');
        elementsToAdapt.forEach((element: any) => {
          // Adapt specific theme colors we know about
          if (theme === 'dark') {
            // Adapt light theme colors to dark theme
            if (element.style.backgroundColor === 'rgb(255, 255, 255)') {
              element.style.backgroundColor = 'transparent';
            }
            if (element.style.color === 'rgb(0, 0, 0)' || element.style.color === 'rgb(24, 28, 50)') {
              element.style.color = 'rgb(255, 255, 255)';
            }
          } else {
            // Adapt dark theme colors to light theme
            if (element.style.backgroundColor === 'rgb(44, 58, 77)' || element.style.backgroundColor === 'rgb(30, 30, 30)') {
              element.style.backgroundColor = 'transparent';
            }
            if (element.style.color === 'rgb(255, 255, 255)' || element.style.color === 'rgb(200, 200, 200)') {
              element.style.color = 'rgb(0, 0, 0)';
            }
          }
        });
      }

      editor.focus(); // Ensure the editor is active
      let range = editor.getSelection();
      // If no selection but editor has focus, get cursor position
      if (!range && editor.hasFocus()) {
        const cursorPosition = editor.getLength() - 1;
        range = { index: cursorPosition, length: 0 };
      }
      // If no selection and no focus, use start of document
      else if (!range) {
        range = { index: 0, length: 0 };
      }

      if (!range) return;
      // Insert with all formatting preserved
      const beforeLength = editor.getLength();
      if (range?.length > 0) editor.deleteText(range.index, range.length);

      // Adaptation mutates elements in place, so re-read from the wrapper — using
      // tempDiv would paste the [data-editor-source] wrapper div as an extra block.
      pasteHtmlWithTables(editor, range?.index, needsThemeAdaptation ? editorContent.innerHTML : contentToInsert);
      // Update editor content
      const newContent = editor.root.innerHTML;
      setEditor(newContent);
      // Position cursor after pasted content using delta length difference (accounts for block delimiters)
      setTimeout(() => {
        const afterLength = editor.getLength();
        const insertedLength = afterLength - beforeLength + range.length;
        const newCursorPos = Math.min(range.index + insertedLength, afterLength - 1);
        editor.setSelection(newCursorPos, 0);

        // Process task ID links after paste - fixes the paste recognition issue
        processTaskIdLinks(editor);
      }, 150);

    }
    else {
      // External content (e.g., from websites, blogs, etc.)
      const contentElement = tempDiv.querySelector('body') || tempDiv;
      const elementsToClean = contentElement.querySelectorAll('*');
      elementsToClean.forEach((element: any) => {
        // Handle color adaptation more intelligently for external content
        // Remove background color if it would cause readability issues in current theme
        const computedStyle = window.getComputedStyle(element);
        const bgColor = element.style.backgroundColor || computedStyle.backgroundColor;
        const textColor = element.style.color || computedStyle.color;
        // Apply smart theme adaptation based on current theme
        if (theme === 'dark') {
          // For dark theme, ensure text is visible
          // Remove light backgrounds and only change near-black text to white
          // Preserve colors like red, blue, green that are readable on dark backgrounds
          if (isLightColor(bgColor)) {
            element.style.removeProperty('background-color');
          }
          if (isNearBlack(textColor)) {
            element.style.color = '#FFFFFF'; // Use light text for dark theme
          }
        } else {
          // For light theme, ensure text is visible
          // Remove dark backgrounds and light text that would be hard to read
          if (isDarkColor(bgColor)) {
            element.style.removeProperty('background-color');
          }
          if (isLightColor(textColor)) {
            element.style.color = '#000000'; // Use dark text for light theme
          }
        }
      });

      // Add a blank line after each original h2 heading (before converting h3-h6)
      const originalH2Headings = contentElement.querySelectorAll('h2');
      originalH2Headings.forEach((heading: any) => {
        const nextSibling = heading.nextElementSibling;
        // Add blank line after h2 if there is content below and it's not already a blank line
        if (nextSibling && !(nextSibling.tagName === 'P' && (nextSibling.innerHTML === '<br>' || nextSibling.innerHTML.trim() === ''))) {
          const blankLineAfter = document.createElement('p');
          blankLineAfter.innerHTML = '<br>';
          heading.parentNode?.insertBefore(blankLineAfter, heading.nextSibling);
        }
      });

      // Convert h3-h6 headings to h2
      const headingsToConvert = contentElement.querySelectorAll('h3, h4, h5, h6');
      headingsToConvert.forEach((heading: any) => {
        const h2 = document.createElement('h2');
        h2.innerHTML = heading.innerHTML;
        // Copy over attributes
        Array.from(heading.attributes).forEach((attr: any) => {
          h2.setAttribute(attr.name, attr.value);
        });
        heading.parentNode?.replaceChild(h2, heading);
      });

      editor.focus(); // Ensure the editor is active
      let range = editor.getSelection();
      // If no selection but editor has focus, get cursor position
      if (!range && editor.hasFocus()) {
        const cursorPosition = editor.getLength() - 1;
        range = { index: cursorPosition, length: 0 };
      }
      // If no selection and no focus, use start of document
      else if (!range) {
        range = { index: 0, length: 0 };
      }
      if (!range) return;
      const cleanHtml = contentElement === tempDiv
        ? tempDiv?.innerHTML
        : contentElement?.innerHTML;
      const beforeLength = editor.getLength();
      if (range?.length > 0) editor.deleteText(range.index, range.length);
      pasteHtmlWithTables(editor, range?.index, cleanHtml);
      // Update state
      const newContent = editor?.root?.innerHTML;
      setEditor(newContent);
      // Position cursor after pasted content using delta length difference (accounts for block delimiters)
      setTimeout(() => {
        const afterLength = editor.getLength();
        const insertedLength = afterLength - beforeLength + range.length;
        const newCursorPos = Math.min(range.index + insertedLength, afterLength - 1);
        editor.setSelection(newCursorPos, 0);

        // Process task ID links after paste - fixes the paste recognition issue
        processTaskIdLinks(editor);

        // Heading colour is owned by the shared DS rich-text stylesheet
        // (text-primary, theme-aware) — see b4igo-type-tokens-web.css [DS-10W].
        // We intentionally no longer bake an inline colour onto pasted headings:
        // hierarchy reads via size + weight, and any colour the user applies
        // manually via the editor tool is preserved.
      }, 150); // Slightly longer timeout to ensure content is fully rendered
    }
  };
  // Utility functions to determine if a color is light or dark
  const isLightColor = (color: string): boolean => {
    if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return false;
    // Extract RGB components
    const rgb = color.match(/\d+/g);
    if (!rgb || rgb.length < 3) return false;
    // Calculate luminance (simplified)
    const luminance = (parseInt(rgb[0]) * 0.299 + parseInt(rgb[1]) * 0.587 + parseInt(rgb[2]) * 0.114) / 255;
    return luminance > 0.6; // Higher values are lighter colors
  };
  const isDarkColor = (color: string): boolean => {
    if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return false;
    // Extract RGB components
    const rgb = color.match(/\d+/g);
    if (!rgb || rgb.length < 3) return false;
    // Calculate luminance (simplified)
    const luminance = (parseInt(rgb[0]) * 0.299 + parseInt(rgb[1]) * 0.587 + parseInt(rgb[2]) * 0.114) / 255;
    return luminance < 0.4; // Lower values are darker colors
  };
  // Check if color is near-black (very dark, unreadable on dark backgrounds)
  // This preserves colors like red, blue, green that are readable on dark backgrounds
  const isNearBlack = (color: string): boolean => {
    if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') return false;
    const rgb = color.match(/\d+/g);
    if (!rgb || rgb.length < 3) return false;
    const r = parseInt(rgb[0]);
    const g = parseInt(rgb[1]);
    const b = parseInt(rgb[2]);
    // Only consider it near-black if all RGB values are very low (< 50)
    return r < 50 && g < 50 && b < 50;
  };

  // Process pasted content for task ID links - handles the paste recognition issue
  const processTaskIdLinks = (editor: any) => {
    if (!boardId) return;

    const content = editor.root.innerHTML;
    const processedContent = processHtml(content);

    // Check for task ID patterns in the pasted content
    const taskIdPattern = /\/[A-Z]+-\d+/g;
    const matches = processedContent.match(taskIdPattern);

    if (matches && matches.length > 0) {
      let updatedContent = processedContent;

      // Process card links
      updatedContent = updatedContent.replace(/\/[^#\s]*\d[^\s]*/g, (match: string) => {
        const cleanMatch = match.replace(/amp;/g, '');
        const matchingCard = allCurrentUserCards?.find((card: any) => {
          const cardPattern = `/${card?.key?.trim()}`;
          return cardPattern === cleanMatch;
        });
        if (matchingCard && !match.includes('<a')) {
          return `<a href="#">${match}</a>`;
        }
        return match;
      });

      // Process page links
      updatedContent = updatedContent.replace(/\/[^#\s]*\d[^\s]*/g, (match: string) => {
        const cleanMatch = match.replace(/amp;/g, '');
        const matchingPage = pages?.find((page: any) => {
          const pagePattern = `/${page?.key?.trim()}`;
          return pagePattern === cleanMatch;
        });
        if (matchingPage && !match.includes('<a')) {
          return `<a href="#">${match}</a>`;
        }
        return match;
      });

      setEditor(updatedContent);

      // Apply link styling to any newly created links
      setTimeout(() => {
        const customLinks = document.querySelectorAll<HTMLAnchorElement>(
          'div.ql-editor[contenteditable="true"] a'
        );

        Array.from(customLinks).forEach((link) => {
          if (/\/\S*-\d+\b/.test(link.innerText)) {
            link.setAttribute(
              'style',
              `max-width: 30%; cursor: pointer; text-decoration: underline; display: inline; color: ${theme === 'dark' ? '#98C6F9' : '#0067c5'};`
            );
            link.setAttribute('id', link.innerText);
            link.classList.add('pattern-class');
            link.addEventListener('mouseover', (e) => {
              e.preventDefault();
              debouncedOnChange(link.innerText.replace(/[/#]/g, '').trim());
            });
            link.setAttribute(
              'data-tooltip-id',
              `my-tooltip-children-multiline-write-${link.innerText.replace(/[#/]/g, '').trim()}`
            );
          }
        });
      }, 50);
    }
  };

  // Trigger mention dropdown for pasted task IDs by directly calling mention module
  const triggerMentionForPastedId = (editor: any, pastedText: string, pasteStartIndex: number) => {
    if (!boardId) return;

    const trimmedText = pastedText.trim();
    // Check if pasted text starts with a mention denotation character
    const mentionPattern = /^[/#@]/;
    if (!mentionPattern.test(trimmedText)) {
      // If doesn't start with mention char, process for existing task links
      processTaskIdLinks(editor);
      return;
    }

    // Position cursor at end of pasted text
    const endPosition = pasteStartIndex + trimmedText.length;
    editor.setSelection(endPosition);

    // Get the mention module and directly trigger the search
    const mentionModule = editor.getModule('mention');
    if (mentionModule && typeof mentionModule.onSomethingChange === 'function') {
      // Small delay to ensure editor state is settled
      setTimeout(() => {
        // Call the mention module's internal method to trigger search
        mentionModule.onSomethingChange();
      }, 50);
    } else {
      // Fallback: process task ID links if mention module not available
      processTaskIdLinks(editor);
    }
  };

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    // Remove any existing listeners first
    editor.root.removeEventListener('paste', handlePaste);
    editor.root.removeEventListener('copy', handleCopy);

    editor.root.addEventListener('paste', handlePaste, { capture: true });
    editor.root.addEventListener('copy', handleCopy, { capture: true });

    return () => {
      editor.root.removeEventListener('paste', handlePaste, { capture: true });
      editor.root.removeEventListener('copy', handleCopy, { capture: true });
    };
  }, [theme]);

  // Save the table cell selection whenever it changes, so toolbar clicks can restore it
  useEffect(() => {
    // Track selection changes — only save when inside a table cell
    const onSelectionChange = () => {
      const sel = window.getSelection()
      if (sel && sel.rangeCount > 0) {
        const anchor = sel.anchorNode
        const inTable = anchor && (
          (anchor as HTMLElement).closest?.('.ql-table-embed') ||
          anchor?.parentElement?.closest?.('.ql-table-embed')
        )
        if (inTable) {
          tableCellSelectionRef.current = sel.getRangeAt(0).cloneRange()
        }
      }
    }
    document.addEventListener('selectionchange', onSelectionChange)

    // Clear the table selection only when user clicks directly into the non-table editor area
    const editor = quillRef.current?.getEditor()
    const onEditorClick = (e: Event) => {
      const target = e.target as HTMLElement
      if (!target.closest('.ql-table-embed')) {
        tableCellSelectionRef.current = null
      }
    }
    editor?.root.addEventListener('mousedown', onEditorClick)

    return () => {
      document.removeEventListener('selectionchange', onSelectionChange)
      editor?.root.removeEventListener('mousedown', onEditorClick)
    }
  }, [])

  // Restore a saved table cell selection and return true if successful
  const restoreTableSelection = (): boolean => {
    const range = tableCellSelectionRef.current
    if (!range) return false
    // Verify the range is still inside a table embed in the DOM
    const container = range.commonAncestorContainer
    const el = container.nodeType === Node.TEXT_NODE ? container.parentElement : container as HTMLElement
    const tableEmbed = el?.closest?.('.ql-table-embed')
    if (!tableEmbed || !document.contains(tableEmbed)) {
      tableCellSelectionRef.current = null
      return false
    }
    const sel = window.getSelection()
    sel?.removeAllRanges()
    sel?.addRange(range.cloneRange())
    return true
  }

  // Apply formatting inside a table cell via native execCommand
  const applyTableCellFormat = (_command: string, _value?: string): boolean => {
    const restored = restoreTableSelection()
    if (!restored) return false
    // Ensure the cell has focus so execCommand works
    const sel = window.getSelection()
    if (sel && sel.rangeCount > 0) {
      const node = sel.anchorNode
      const el = node?.nodeType === Node.TEXT_NODE ? node.parentElement : node as HTMLElement
      const cell = el?.closest?.('td, th') as HTMLElement
      if (cell) cell.focus()
      // Re-apply the selection after focus (focus can collapse it)
      sel.removeAllRanges()
      sel.addRange(tableCellSelectionRef.current?.cloneRange() as Range)
    }
    document.execCommand(_command, false, _value || '')
    return true
  }

  // Intercept toolbar button clicks when focus is in a table cell
  useEffect(() => {
    const editor = quillRef.current?.getEditor()
    if (!editor) return

    const toolbarEl = editor.root.closest('.quill')?.querySelector('.ql-toolbar') as HTMLElement | null
    if (!toolbarEl) return

    // Map of Quill toolbar CSS classes to execCommand names
    const formatMap: Record<string, string> = {
      'ql-bold': 'bold',
      'ql-italic': 'italic',
      'ql-underline': 'underline',
      'ql-strike': 'strikeThrough',
    }

    // Force-enable pickers that Quill disables when there's no Quill selection
    const enablePickers = () => {
      toolbarEl.querySelectorAll('.ql-picker.ql-disabled, .ql-header.ql-disabled, .ql-size.ql-disabled, .ql-align.ql-disabled').forEach(p => {
        p.classList.remove('ql-disabled')
      })
      toolbarEl.querySelectorAll('button[disabled]').forEach(b => {
        b.removeAttribute('disabled')
      })
    }

    const handleToolbarMouseDown = (e: Event) => {
      const target = e.target as HTMLElement
      const btn = target.closest('button') || target.closest('.ql-picker-label')
      if (!btn) return
      if (!tableCellSelectionRef.current) return

      // Force enable pickers in case Quill disabled them
      enablePickers()

      // Check basic formatting buttons
      for (const [cls, cmd] of Object.entries(formatMap)) {
        if (btn.classList.contains(cls)) {
          e.preventDefault()
          e.stopPropagation()
          applyTableCellFormat(cmd)
          return
        }
      }

      // Header picker — let the dropdown open (don't prevent/stop), just enable it
      if (btn.closest('.ql-header')) {
        enablePickers()
        // Manually toggle the picker open since Quill won't
        const picker = btn.closest('.ql-picker') as HTMLElement
        if (picker && !picker.classList.contains('ql-expanded')) {
          picker.classList.add('ql-expanded')
          e.preventDefault()
          e.stopPropagation()
        }
        return
      }

      // Size picker — same approach
      if (btn.closest('.ql-size')) {
        enablePickers()
        const picker = btn.closest('.ql-picker') as HTMLElement
        if (picker && !picker.classList.contains('ql-expanded')) {
          picker.classList.add('ql-expanded')
          e.preventDefault()
          e.stopPropagation()
        }
        return
      }

      // List buttons
      if (btn.classList.contains('ql-list')) {
        e.preventDefault()
        e.stopPropagation()
        const val = btn.getAttribute('value')
        applyTableCellFormat(val === 'ordered' ? 'insertOrderedList' : 'insertUnorderedList')
        return
      }

      // Alignment
      if (btn.closest('.ql-align')) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
    }

    // Handle picker item clicks (headers, sizes, alignment)
    // Also block click events for basic format buttons so Quill doesn't
    // re-apply formatting to the entire table embed after execCommand already handled it.
    const handleToolbarClick = (e: Event) => {
      const target = e.target as HTMLElement

      // Block click on basic format / list buttons when editing inside a table cell.
      // The mousedown handler already applied the format via execCommand;
      // letting the click reach Quill would cause it to format the whole embed.
      if (tableCellSelectionRef.current) {
        const btn = target.closest('button')
        if (btn) {
          for (const cls of Object.keys(formatMap)) {
            if (btn.classList.contains(cls)) {
              e.preventDefault()
              e.stopPropagation()
              return
            }
          }
          if (btn.classList.contains('ql-list')) {
            e.preventDefault()
            e.stopPropagation()
            return
          }
        }
      }

      const item = target.closest('.ql-picker-item')
      if (!item) return
      if (!tableCellSelectionRef.current) return

      const picker = item.closest('.ql-picker')
      if (!picker) return

      // Header picker
      if (picker.classList.contains('ql-header')) {
        e.preventDefault()
        e.stopPropagation()
        const val = item.getAttribute('data-value')
        if (val === '1' || val === '2') {
          applyTableCellFormat('formatBlock', `<h${val}>`)
        } else {
          applyTableCellFormat('formatBlock', '<p>')
        }
        picker.classList.remove('ql-expanded')
        return
      }

      // Size picker
      if (picker.classList.contains('ql-size')) {
        e.preventDefault()
        e.stopPropagation()
        const val = item.getAttribute('data-value')
        const sizeMap: Record<string, string> = { 'small': '2', 'large': '5' }
        applyTableCellFormat('fontSize', sizeMap[val || ''] || '3')
        picker.classList.remove('ql-expanded')
        return
      }

      // Alignment picker
      if (picker.classList.contains('ql-align')) {
        e.preventDefault()
        e.stopPropagation()
        const val = item.getAttribute('data-value') || 'left'
        if (restoreTableSelection()) {
          const sel = window.getSelection()
          const cell = sel?.anchorNode && (
            (sel.anchorNode as HTMLElement).closest?.('td, th') ||
            sel.anchorNode.parentElement?.closest('td, th')
          )
          if (cell) (cell as HTMLElement).style.textAlign = val
        }
        picker.classList.remove('ql-expanded')
        return
      }
    }

    toolbarEl.addEventListener('mousedown', handleToolbarMouseDown, true)
    toolbarEl.addEventListener('click', handleToolbarClick, true)

    return () => {
      toolbarEl.removeEventListener('mousedown', handleToolbarMouseDown, true)
      toolbarEl.removeEventListener('click', handleToolbarClick, true)
    }
  }, [])

  const processHtml = (content: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = content;
    // Process elements in the content for theme-appropriate styling
    let needsStyleAdaptation = false;
    const elementsToProcess = tempDiv.querySelectorAll('*');
    elementsToProcess.forEach((element: any) => {
      // Skip processing elements that shouldn't have their styles changed
      if (element.classList.contains('mention') ||
        element.tagName === 'A' ||
        element.classList.contains('pattern-class')) {
        return;
      }
      const bgColor = element.style.backgroundColor;
      const textColor = element.style.color;
      if (bgColor || textColor) {
        // Check if colors need adaptation for current theme
        if (theme === 'dark') {
          // For dark theme
          if (bgColor && isLightColor(bgColor)) {
            // element.style.backgroundColor = 'transparent';
            needsStyleAdaptation = true;
          }
          if (textColor && isDarkColor(textColor)) {
            // element.style.color = '#FFFFFF';
            needsStyleAdaptation = true;
          }
        } else {
          // For light theme
          if (bgColor && isDarkColor(bgColor)) {
            // element.style.backgroundColor = 'transparent';
            needsStyleAdaptation = true;
          }
          if (textColor && isLightColor(textColor) && textColor !== 'rgb(0, 103, 197)') {
            // Preserve blue link color but adapt other light colors
            // element.style.color = '#000000';
            needsStyleAdaptation = true;
          }
        }
      }
    });
    const processedContent = needsStyleAdaptation ? tempDiv.innerHTML : content;
    return processedContent

  }
  const handleChange = (content: string, delta: any, source: string) => {
    // A formatting-only change (e.g. switching a line from Heading 1 to
    // Heading 2) carries only `retain` ops — the text is untouched, so it
    // can't introduce new card/page links. Push the raw Quill HTML straight
    // back: it already equals what the controlled ReactQuill holds, so
    // react-quill skips the setContents reconcile that otherwise yanks the
    // scroll to the top. (Text inserts still take the transform path below.)
    const isFormatOnlyChange =
      Array.isArray(delta?.ops) &&
      delta.ops.length > 0 &&
      !delta.ops.some((op: any) => op.insert != null || op.delete != null)

    if (source === 'user' && isFormatOnlyChange) {
      setEditor(content)
    } else if (source === 'user') {
      // Skip the DOM walk on plain typing — only run when paste/theme flagged it
      const processedContent = needsProcessHtmlRef.current
        ? processHtml(content)
        : content
      needsProcessHtmlRef.current = false

      if (boardId) {
        const replacedContent = processedContent.replace(/\/[^#\s]*\d[^\s]*/g, (match) => {
          const replacedContent1 = match.replace(/amp;/g, '');
          const matchingCard = allCurrentUserCards?.find((card: any) => {
            const cardPattern = `/${card?.key?.trim()}`
            return cardPattern === replacedContent1
          })
          if (matchingCard) {
            return `<a href="#">${match}</a>`
          } else {
            return match
          }
        })
        const replacedContentForPage = replacedContent.replace(/\/[^#\s]*\d[^\s]*/g, (match) => {
          const replacedContent1 = match.replace(/amp;/g, '');
          const matchingPage = pages?.find((page: any) => {
            const pagePattern = `/${page?.key?.trim()}`
            return pagePattern === replacedContent1
          })
          if (matchingPage) {
            return `<a href="#">${match}</a>`
          } else {
            return match
          }
        })
        setEditor(replacedContentForPage)
        // Defer DOMParser-heavy mention extraction to trailing edge of typing
        debouncedExtractMentions(processedContent)
      } else {
        setEditor(processedContent)
      }
    }

    const customLinks = document.querySelectorAll<HTMLAnchorElement>(
      'div.ql-editor[contenteditable="true"] a'
    )

    if (customLinks.length > 0) {
      Array.from(customLinks).forEach((link) => {
        if (/\/\S*-\d+\b/.test(link.innerText)) {
          link.setAttribute(
            'style',
            `max-width: 30%; cursor: pointer; text-decoration: underline; display: inline; color: ${theme === 'dark' ? '#98C6F9' : '#0067c5'};`
          )

          link.setAttribute('id', link.innerText)

          link.classList.add('pattern-class')
          link.addEventListener('mouseover', (e) => {
            e.preventDefault()
            debouncedOnChange(link.innerText.replace(/[/#]/g, '').trim())
          })
          link.setAttribute(
            'data-tooltip-id',
            `my-tooltip-children-multiline-write-${link.innerText.replace(/[#/]/g, '').trim()}`
          );


          // link.addEventListener('click', (e) => {
          //   e.preventDefault()
          //   if (handleFormSubmission) {
          //     debugger
          //     handleFormSubmission()
          //   }
          //   if(!linkClickLoading){
          //     setCard(
          //       allCurrentUserCards?.find(
          //         (card: any) => `${card?.key.trim()}` === link.innerText.replace('/', '').trim()
          //       )
          //     )

          //     setShowEditModalQuill(true)
          //     setCardSelected(null)
          //   }

          // })
        }
      })
    }

    const mentionsEle = document.querySelectorAll<HTMLAnchorElement>(
      'div.ql-editor[contenteditable="true"] .mention'
    )

    Array.from(mentionsEle).forEach((link) => {
      link.setAttribute(
        'data-tooltip-id',
        `my-tooltip-children-multiline-write-${link
          ?.querySelector('span')
          ?.innerHTML.trim()
          ?.replace(/[#/]/g, '')}`
      )
    })

    // Heading colour is owned by the shared DS rich-text stylesheet
    // (text-primary, theme-aware) — see b4igo-type-tokens-web.css [DS-10W].
    // No inline colour is applied here, so hierarchy reads via size + weight
    // and manual heading colours chosen via the editor tool are preserved.

    handleMedia()
  }

  const onBlur = (previousSelection: any, source: any, editor: any) => {
    const content = editor.getHTML()

    const processedContent = processHtml(content)

    const upContent = processedContent.replaceAll('</p>', '  </p>')
    const nextCont = upContent.replaceAll('<p>', '  <p>  ')
    if (source === 'user') {
      if (boardId) {
        const replacedContent = nextCont.replace(/\/[^#\s]*\d[^\s]*/g, (match: any) => {
          const matchingCard = allCurrentUserCards?.find((card: any) => {
            const cardPattern = `/${card?.key.trim()}`
            return cardPattern === match
          })
          if (matchingCard) {
            return `<a href="#">${match}</a>`
          } else {
            return match
          }
        })

        const replacedContentForPage = nextCont.replace(/\/[^#\s]*\d[^\s]*/g, (match: any) => {
          const replacedContent1 = match.replace(/amp;/g, '');
          const matchingPage = pages?.find((page: any) => {
            const pagePattern = `/${page?.key?.trim()}`
            return pagePattern === replacedContent1
          })
          if (matchingPage) {
            return `<a href="#">${match}</a>`
          } else {
            return match
          }
        })
        setEditor(replacedContent)
        setEditor(replacedContentForPage)
        setMentions && extractMentionText(processedContent, setMentions, retainMentions)

      } else {
        setEditor(processedContent)
      }

    }
    const customLinks = document.querySelectorAll<HTMLAnchorElement>(
      'div.ql-editor[contenteditable="true"] a'
    )


    if (customLinks.length > 0) {
      Array.from(customLinks).forEach((link) => {
        if (/\/\S*-\d+\b/.test(link.innerText)) {
          link.setAttribute(
            'style',
            `max-width: 30%; cursor: pointer; text-decoration: underline; display: inline; color: ${theme === 'dark' ? '#98C6F9' : '#0067c5'};`
          )

          link.setAttribute('id', link.innerText)

          link.classList.add('pattern-class')
          link.addEventListener('mouseover', () => {
            debouncedOnChange(link.innerText.replace(/#]/g, '').trim())
          })
          link.setAttribute(
            'data-tooltip-id',
            `my-tooltip-children-multiline-write-${link.innerText.replace(/[/#]/g, '').trim()}`
          );


          // link.addEventListener('click', (e) => {
          //   e.preventDefault()
          //   if (handleFormSubmission) {
          //     handleFormSubmission()
          //   }
          //   if(!linkClickLoading){
          //     setCard(
          //       allCurrentUserCards?.find(
          //         (card: any) => `${card?.key.trim()}` === link.innerText.replace('/', '').trim()
          //       )
          //     )

          //     setShowEditModalQuill(true)
          //     setCardSelected(null)
          //   }
          // })
        }
      })
    }
  }

  function handleMedia() {
    const editImg: any = document.querySelectorAll(`div.ql-editor[contenteditable="true"] img`)
    const videoIframe: any = document.querySelectorAll(
      `div.ql-editor[contenteditable="true"] iframe`
    )

    if (videoIframe.length > 0) {
      setIsVideoSrcGet(true)
    }

    for (let i = 0; i < editImg.length; i++) {
      editImg[i].style.maxWidth = '30%'
      editImg[i].classList.add('m-2')
      editImg[i].classList.add('cursor-pointer')

      editImg[i].addEventListener('click', () => {
        const imageSrc: any = editImg[i].getAttribute('src')
        editRef.current?.handleModal(imageSrc)
      })
    }
  }

  const onHover = (event: any) => {
    handleMouseover(event.value.id)
  }

  const onMentionClick = (event: any) => {
    // Only handle if the click originated within this editor instance
    const clickTarget = event.event?.target || event.event?.srcElement
    const editorRoot = quillRef.current?.getEditor?.()?.root || editRef.current
    if (editorRoot && clickTarget && !editorRoot.contains(clickTarget)) {
      return
    }

    if (event.value.denotationChar === '@' || !handleFormSubmission) {
      return
    }

    if (event.value.denotationChar === '/') {
      // Push current card/page to history before opening the new one
      if (currentCardOrPage && currentCardOrPage.id) {
        pushToHistory(currentCardOrPage)
      }
      handleFormSubmission()
      setExpandedFromSource(!!isExpandedModalRef.current)
      setCard(
        allCurrentUserCards?.find((card: any) => `${card?.key.trim()}` === (event.value.id?.trim() || event.value.value?.trim())))
      setShowEditModalQuill(true)
      setCardSelected(null)
      return
    }

    // Page mention (#). onMentionClick is bound once (useEffect deps []), so the
    // directly-closed-over `pages` (redux pagesByLoggedInUser) is the EMPTY
    // first-render value and never updates — which is why a page that loaded
    // after mount wouldn't open. Read the live list through `pagesRef` and also
    // check the board-scoped #-mention cache. All in-memory, no extra API — so a
    // page that isn't in either source (e.g. another user's page you haven't
    // surfaced via `#` this session) won't resolve here by design.
    const targetId = `${event.value.id}`
    const effectiveBoardId = boardIdRef.current || currentCardOrPageRef.current?.boardId
    const allCachedPages = Object.values(boardPagesCacheRef.current).flat()
    const resolvedPage =
      pagesRef.current?.find((page: any) => `${page?.id}` === targetId) ||
      (effectiveBoardId &&
        boardPagesCacheRef.current[effectiveBoardId]?.find((p: any) => `${p?.id}` === targetId)) ||
      allCachedPages.find((p: any) => `${p?.id}` === targetId)
    if (!resolvedPage) {
      return
    }
    if (currentCardOrPage && currentCardOrPage.id) {
      pushToHistory(currentCardOrPage)
    }
    handleFormSubmission()
    setExpandedFromSource(!!isExpandedModalRef.current)
    setCard(resolvedPage)
    setShowEditModalQuill(true)
    setCardSelected(null)
  }

  const mentionDebounceOnHover = debounce(onHover, 300)

  useEffect(() => {
    window.addEventListener('mention-hovered', mentionDebounceOnHover, false)
    window.addEventListener('mention-clicked', onMentionClick, false)
    if (editor) {
      handleChange(editor, 0, 'user')
    }
    return () => {
      window.removeEventListener('mention-hovered', mentionDebounceOnHover, false)
      window.removeEventListener('mention-clicked', onMentionClick, false)
    }
    //eslint-disable-next-line
  }, [])

  const scrollToMention = () => {
    if (editor) {
      const quillEditor = quillRef.current.getEditor();
      const targetMention = [...quillEditor.root.querySelectorAll("p span")].find(span => span.getAttribute("data-unique-id") === uniqueId);
      if (!targetMention) {
        return;
      }
      //scroll to uniqueid mention
      const headingElement = targetMention;
      const rect = headingElement.getBoundingClientRect();
      const editorRect = quillEditor.root.getBoundingClientRect();
      const scrollTop = rect.top - editorRect.top + quillEditor.root.scrollTop;
      quillEditor.root.scrollTop = scrollTop;
    }
  };

  // Nearest ancestor that actually scrolls. Starting from the heading and
  // walking up means we pick the editor's own scroll container first, so we can
  // move only that one and leave the surrounding page/modal untouched.
  const getScrollableAncestor = (el: HTMLElement): HTMLElement | null => {
    let node: HTMLElement | null = el.parentElement;
    while (node) {
      const { overflowY } = window.getComputedStyle(node);
      if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  };

  const scrollToHeading = (): boolean => {
    if (!editor) return false;
    const quillEditor = quillRef.current?.getEditor();
    if (!quillEditor) return false;
    const headingElements = quillEditor.root.querySelectorAll('h1, h2');
    const headingElement = headingElements[tocHeading?.index] as HTMLElement | undefined;
    if (!headingElement) return false;

    // Scroll only the editor's own scroll container so the outer modal/page
    // doesn't move (scrollIntoView would scroll every scrollable ancestor).
    const scrollContainer = getScrollableAncestor(headingElement) || quillEditor.root;
    const containerRect = scrollContainer.getBoundingClientRect();
    const headingRect = headingElement.getBoundingClientRect();
    const top = headingRect.top - containerRect.top + scrollContainer.scrollTop;
    scrollContainer.scrollTo({ top, behavior: 'smooth' });
    return true;
  };

  const getHtmlTags = (editor: string) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(editor, 'text/html');

    const headings = doc.querySelectorAll<HTMLHeadingElement>('h1, h2');

    const combinedTags = Array.from(headings).map((tag, index) => ({
      name: tag.textContent,
      tagName: tag.tagName.toLowerCase(),
      index,
    }));

    setEditorTags(combinedTags);
  }

  useEffect(() => {
    if (editor) {
      getHtmlTags(editor)
    }
  }, [editor])

  // Only scroll when the user actually picks a TOC heading — not on every edit.
  // `editor` stays in the deps so a heading seeded before the content loads
  // (e.g. EditPageModal's sendTocHeading) still scrolls once the text arrives;
  // the ref guard makes sure editing a heading afterwards doesn't re-scroll.
  const scrolledTocHeadingRef = useRef<any>(null)
  useEffect(() => {
    if (tocHeading && tocHeading !== scrolledTocHeadingRef.current) {
      if (scrollToHeading()) {
        scrolledTocHeadingRef.current = tocHeading
      }
    }
  }, [tocHeading, editor])
  useEffect(() => {
    if (uniqueId) {
      scrollToMention()
    }
  }, [uniqueId, editor])

  // Table insert handlers
  const handleTableButtonClick = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      savedCursorPosition.current = quill.getSelection();
    }
    setShowTableModal(true);
  };

  const handleTableInsert = (rows: number, cols: number) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = savedCursorPosition.current || quill.getSelection();
    const insertIndex = range ? range.index : quill.getLength() - 1;

    // Build table HTML
    let tableHtml = '<table><thead><tr>';
    for (let c = 0; c < cols; c++) {
      tableHtml += `<th>Heading ${c + 1}</th>`;
    }
    tableHtml += '</tr></thead><tbody>';
    for (let r = 1; r < rows; r++) {
      tableHtml += '<tr>';
      for (let c = 0; c < cols; c++) {
        tableHtml += '<td><br></td>';
      }
      tableHtml += '</tr>';
    }
    tableHtml += '</tbody></table>';

    // Insert as a block embed — the TableEmbed blot handles making cells editable
    quill.insertEmbed(insertIndex, 'tableEmbed', tableHtml, 'user');
    setEditor(quill.root.innerHTML);

    // Focus the first header cell so user can start editing immediately
    setTimeout(() => {
      const tableEmbeds = quill.root.querySelectorAll('.ql-table-embed');
      const lastTable = tableEmbeds[tableEmbeds.length - 1];
      if (lastTable) {
        const firstTh = lastTable.querySelector('th');
        if (firstTh) {
          const sel = window.getSelection();
          const rng = document.createRange();
          rng.selectNodeContents(firstTh);
          sel?.removeAllRanges();
          sel?.addRange(rng);
        }
      }
    }, 50);

    savedCursorPosition.current = null;
  };

  // ============================================================================
  // Checklist — insertion, inline "+" dropdown, custom event wiring
  // ============================================================================

  // Focus the heading of the most recently inserted checklist
  const focusLastChecklistHeading = () => {
    setTimeout(() => {
      const quill = quillRef.current?.getEditor()
      if (!quill) return
      const nodes = quill.root.querySelectorAll('.ql-checklist-embed')
      const last = nodes[nodes.length - 1] as HTMLElement | undefined
      if (!last) return
      const heading = last.querySelector<HTMLElement>('.ql-checklist-heading-title')
      heading?.focus()
    }, 50)
  }

  // Insert a fresh checklist at the current cursor (used by the toolbar "+" button)
  const insertChecklistAtCursor = () => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const range = quill.getSelection() || { index: quill.getLength() - 1, length: 0 }
    quill.insertEmbed(range.index, 'checklistEmbed', buildEmptyChecklistHtml(), 'user')
    // Drop a trailing paragraph so the user can exit the checklist with Enter-Enter
    quill.insertText(range.index + 1, '\n', 'user')
    setEditor(quill.root.innerHTML)
    focusLastChecklistHeading()
  }

  // Insert a checklist in place of a typed "+" at the given Quill index.
  // Used by the inline "+" dropdown flow: removes the one-character "+" and
  // inserts the blot, then focuses the heading.
  const insertChecklistReplacingPlus = (plusIndex: number) => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    quill.deleteText(plusIndex, 1, 'user')
    quill.insertEmbed(plusIndex, 'checklistEmbed', buildEmptyChecklistHtml(), 'user')
    quill.insertText(plusIndex + 1, '\n', 'user')
    setEditor(quill.root.innerHTML)
    focusLastChecklistHeading()
  }

  // Reattach blot handlers and wire up custom events on every content change —
  // paste / undo / remount can drop the handlers otherwise.
  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const root = quill.root as HTMLElement
    const nodes = root.querySelectorAll<HTMLElement>('.ql-checklist-embed')
    nodes.forEach((n) => {
      n.setAttribute('contenteditable', 'false')
      attachChecklistHandlers(n)
    })
  }, [editor])

  // Listen for the custom events the blot dispatches
  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const root = quill.root as HTMLElement

    const onDeleteRequest = (e: Event) => {
      const target = e.target as HTMLElement
      const embed = target.closest?.('.ql-checklist-embed') as HTMLElement | null
      if (!embed) return
      pendingChecklistDeleteRef.current = embed
      setShowChecklistDeleteConfirm(true)
    }

    const onItemDeleteRequest = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { itemId: string; taskKey: string }
        | undefined
      if (!detail?.itemId) return
      setItemDeleteConfirm({ itemId: detail.itemId, taskKey: detail.taskKey })
    }

    const onExit = (e: Event) => {
      const target = e.target as HTMLElement
      const embed = target.closest?.('.ql-checklist-embed') as HTMLElement | null
      if (!embed) return
      // Find the Quill index of the blot and move the cursor to the line after it
      const blot = (Quill as any).find(embed)
      if (!blot) return
      const index = quill.getIndex(blot)
      const afterIndex = index + 1
      // Ensure there's a line to land on
      const length = quill.getLength()
      if (afterIndex >= length) {
        quill.insertText(length - 1, '\n', 'user')
      }
      quill.focus()
      quill.setSelection(afterIndex, 0)
    }

    const onMutated = () => {
      // Persist any DOM-level mutation (drag reorder, assignee change, etc.)
      // back into the editor state so it survives save/reload.
      setEditor(quill.root.innerHTML)
    }

    const onAssigneeOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { item: HTMLElement; buttonRect: { top: number; bottom: number; left: number; right: number } }
        | undefined
      if (!detail?.item) return
      // If the picker for this item was just closed (e.g. by the user
      // clicking the assignee button to dismiss it), ignore the re-open
      // that the subsequent `click` event would otherwise trigger.
      const itemId =
        detail.item.getAttribute('data-checklist-item-id') ?? ''
      const last = lastAssigneeCloseRef.current
      if (last && last.itemId === itemId && Date.now() - last.at < 300) {
        lastAssigneeCloseRef.current = null
        return
      }
      let initial = readChecklistAssignees(detail.item) as unknown as ChecklistAssigneeDropdownType[]
      // Linked items: board assignees live on the task. Union the DOM
      // stamp (written at creation and after each PATCH) AND redux's
      // current view of the card. Treating either as authoritative on
      // its own causes drops — the stamp can lag a re-render that
      // rebuilds blots, and redux's getAllCard shape strips
      // board_person_id. The union keeps both viewpoints.
      const taskId = detail.item.getAttribute('data-checklist-task-id')
      const taskKey = detail.item.getAttribute('data-checklist-task-key')
      if (taskId) {
        const boardMembers = boardPersonsForSendAlertRef.current || []
        const normalize = (s: string) => (s ?? '').trim().toLowerCase()

        type TaskAssigneeShape = { board_person_id?: string; fullName?: string; userId?: string }
        const fromStamp: TaskAssigneeShape[] = []
        const stamp = detail.item.getAttribute('data-checklist-task-bpids')
        if (stamp) {
          try {
            const parsed = JSON.parse(stamp)
            if (Array.isArray(parsed)) {
              parsed.forEach((p) => {
                if (p && typeof p === 'object') fromStamp.push(p as TaskAssigneeShape)
              })
            }
          } catch {
            /* ignore malformed stamp */
          }
        }
        const fromRedux: TaskAssigneeShape[] = []
        const match = allCurrentUserCardsRef.current?.find(
          (c: any) =>
            c?.id === taskId ||
            (typeof c?.key === 'string' && taskKey && c.key.trim() === taskKey)
        )
        if (Array.isArray(match?.assignees)) {
          match.assignees.forEach((a: any) => {
            if (typeof a === 'string') {
              if (a.trim()) fromRedux.push({ fullName: a })
            } else if (a && typeof a === 'object') {
              fromRedux.push({
                board_person_id: a?.board_person_id,
                fullName: a?.fullName ?? a?.name,
                userId: a?.userId,
              })
            }
          })
        }
        // Dedupe across BOTH sources by ANY identity an entry exposes —
        // userId, bpid, OR normalized name. The same person from stamp
        // (userId+bpid) and redux (bpid only) would otherwise register
        // different keys and slip through. Each entry contributes every
        // key it has to the seen set so any subsequent entry sharing
        // any one of them is dropped.
        const seenKey = new Set<string>()
        const taskAssigneeRaw: TaskAssigneeShape[] = []
        const keysOf = (entry: TaskAssigneeShape): string[] => {
          const out: string[] = []
          const uid = entry.userId?.trim()
          const bpid = entry.board_person_id?.trim()
          const nameKey = normalize(entry.fullName ?? '')
          if (uid) out.push(`u:${uid}`)
          if (bpid) out.push(`bp:${bpid}`)
          if (nameKey) out.push(`n:${nameKey}`)
          return out
        }
        const pushUnique = (entry: TaskAssigneeShape) => {
          const keys = keysOf(entry)
          if (keys.length === 0) return
          if (keys.some((k) => seenKey.has(k))) return
          keys.forEach((k) => seenKey.add(k))
          taskAssigneeRaw.push(entry)
        }
        // Stamp first so its userId+bpid claim all keys before redux
        // entries (which may only have bpid or only name) get a chance.
        fromStamp.forEach(pushUnique)
        fromRedux.forEach(pushUnique)

        // Match each raw entry to the board roster — userId first
        // (boardMembers[i].id IS userId, so this is a direct hit when
        // the stamp carries it) then by name as a fallback. Anything
        // we can't match becomes a placeholder carrying `_bpid` so the
        // SEND side can still resolve it.
        const taskBoardAssignees: ChecklistAssigneeDropdownType[] = taskAssigneeRaw
          .map((a) => {
            const displayName = a?.fullName ?? ''
            const bpid = a?.board_person_id ?? ''
            const uid = a?.userId ?? ''
            let member: any = null
            if (uid) {
              member = boardMembers.find((m: any) => m?.id === uid) ?? null
            }
            if (!member) {
              const needle = normalize(displayName)
              member = needle
                ? boardMembers.find((m: any) => normalize(m?.name) === needle) ?? null
                : null
            }
            if (member) {
              return {
                id: member.id,
                name: displayName || member.name,
                source: 'board' as const,
                email: member.email,
                _bpid: bpid || undefined,
              } as ChecklistAssigneeDropdownType
            }
            if (!displayName.trim() && !bpid) return null
            return {
              id: bpid || displayName.trim(),
              name: displayName,
              source: 'board' as const,
              _bpid: bpid || undefined,
            } as ChecklistAssigneeDropdownType
          })
          .filter((x: any): x is ChecklistAssigneeDropdownType => !!x)

        const nonBoardLocal = initial.filter((a) => a.source !== 'board')
        const seen = new Set<string>()
        const mergedBoard = taskBoardAssignees.filter((a) => {
          const key = a._bpid ?? a.id
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
        initial = [...nonBoardLocal, ...mergedBoard]
      }
      setAssigneePicker({
        anchorRect: detail.buttonRect,
        item: detail.item,
        initial,
      })
    }

    const onReminderOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { item: HTMLElement; buttonRect: { top: number; bottom: number; left: number; right: number } }
        | undefined
      if (!detail?.item) return
      setReminderPicker({
        anchorRect: detail.buttonRect,
        item: detail.item,
        initial: readChecklistDueDate(detail.item),
      })
    }

    const onCreateTask = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { itemId: string; text: string; checklistTitle?: string }
        | undefined
      if (!detail?.itemId) return
      // Snapshot the item's existing assignees + due date NOW so the
      // Create button reads from a stable state (not the live DOM at
      // submit time, which a Quill rebuild between open and confirm
      // could swap out from under us).
      const quill = quillRef.current?.getEditor()
      const item = quill?.root.querySelector(
        `[data-checklist-item-id="${detail.itemId}"]`
      ) as HTMLElement | null
      const assignees = item
        ? (readChecklistAssignees(item) as unknown as ChecklistAssigneeDropdownType[])
        : []
      const { due_date, due_time } = item
        ? readChecklistDueDateRaw(item)
        : { due_date: null, due_time: null }
      setTaskFromChecklist({
        itemId: detail.itemId,
        text: detail.text,
        checklistTitle: detail.checklistTitle || 'Checklist',
        assignees,
        due_date,
        due_time,
      })
    }

    const onTaskPreview = (e: Event) => {
      const detail = (e as CustomEvent).detail as
        | { itemId: string; taskId: string; taskKey: string; title: string }
        | undefined
      if (!detail?.taskId) return
      setTaskPreview({ taskId: detail.taskId, taskKey: detail.taskKey, title: detail.title })
    }

    root.addEventListener('checklist-delete-request', onDeleteRequest)
    root.addEventListener('checklist-item-delete-request', onItemDeleteRequest)
    root.addEventListener('checklist-exit', onExit)
    root.addEventListener('checklist-mutated', onMutated)
    root.addEventListener('checklist-assignee-open', onAssigneeOpen)
    root.addEventListener('checklist-reminder-open', onReminderOpen)
    root.addEventListener('checklist-create-task', onCreateTask)
    root.addEventListener('checklist-task-preview', onTaskPreview)
    return () => {
      root.removeEventListener('checklist-delete-request', onDeleteRequest)
      root.removeEventListener('checklist-item-delete-request', onItemDeleteRequest)
      root.removeEventListener('checklist-exit', onExit)
      root.removeEventListener('checklist-mutated', onMutated)
      root.removeEventListener('checklist-assignee-open', onAssigneeOpen)
      root.removeEventListener('checklist-reminder-open', onReminderOpen)
      root.removeEventListener('checklist-create-task', onCreateTask)
      root.removeEventListener('checklist-task-preview', onTaskPreview)
    }
  }, [])

  // Seed the column choice each time the create-task-from-checklist modal
  // opens. Default priority: the last column used for this board (remembered
  // per board), else a column previously created via this flow (auto-named
  // "Before I go"), else the first column on the board. Retries until columns
  // have loaded; won't re-seed the same open once a column id is set (so a
  // mid-modal refetch can't clobber the selection).
  useEffect(() => {
    if (!taskFromChecklist) {
      columnSeededItemRef.current = null
      return
    }
    if (
      columnSeededItemRef.current === taskFromChecklist.itemId &&
      columnChoice.columnId
    ) {
      return
    }
    const ordered = [...(columns || [])].sort(
      (a, b) => (Number(a?.position) || 0) - (Number(b?.position) || 0)
    )
    if (ordered.length === 0) {
      // Board has no kanban yet (typical for a note's board). Go straight to
      // the new-column editor — there's nothing to pick. Seed once per open.
      if (columnSeededItemRef.current !== taskFromChecklist.itemId) {
        columnSeededItemRef.current = taskFromChecklist.itemId
        setColumnChoice({
          mode: 'new',
          columnId: '',
          newName: CHECKLIST_COLUMN_DEFAULT_NAME,
          newColor: CHECKLIST_COLUMN_DEFAULT_COLOR,
        })
      }
      return
    }
    // Last column used on this board (if it still exists), then a
    // checklist-created "Before I go" column, then the first column.
    const rememberedId = getLastChecklistColumn(boardId || currentCardOrPage?.boardId)
    const remembered = rememberedId
      ? ordered.find((c) => c.id === rememberedId)
      : undefined
    const beforeIGo = ordered.find(
      (c) =>
        (c?.name || '').trim().toLowerCase() ===
        CHECKLIST_COLUMN_DEFAULT_NAME.toLowerCase()
    )
    const defaultCol = remembered || beforeIGo || ordered[0]
    columnSeededItemRef.current = taskFromChecklist.itemId
    setColumnChoice({
      mode: 'select',
      columnId: defaultCol?.id || '',
      newName: CHECKLIST_COLUMN_DEFAULT_NAME,
      newColor: CHECKLIST_COLUMN_DEFAULT_COLOR,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskFromChecklist, columns])

  // Toggle a class on the editor root so CSS can hide the "T" button.
  // Create-task-from-checklist is allowed when editing a SAVED task OR a
  // SAVED note (page) — both now route through the same modal with a column
  // picker. The semantic gate is "host passed an `onCreateTaskFromChecklistItem`
  // callback AND `currentCardOrPage` is persisted (has an id)". Only the
  // task (UpdateCardForm) and note (EditPageForm) edit forms pass that
  // callback — new/unsaved modals (AddCardForm, AddPageForm) never do — so
  // the callback's presence already excludes the create-new flows. We no
  // longer require `board_column_id`: notes have no inherited column, the
  // picker supplies (or creates) one.
  useEffect(() => {
    if (!enableChecklist) return
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const root = quill.root as HTMLElement
    const isSavedEntity = !!currentCardOrPage?.id
    if (onCreateTaskFromChecklistItem && isSavedEntity) {
      root.classList.remove('ql-checklist-task-disabled')
    } else {
      root.classList.add('ql-checklist-task-disabled')
    }
  }, [
    enableChecklist,
    onCreateTaskFromChecklistItem,
    currentCardOrPage?.id,
  ])

  // After every editor change, restore the cached per-item attrs onto the
  // live DOM. Quill's clipboard.convert round-trip — triggered any time we
  // call setEditor — strips arbitrary data-* attrs off the inner item
  // divs, so attributes set immediately before setEditor (assignee picks,
  // due-date writes, task-link stamps after Create Task) come back empty
  // on the next render. CSS-driven styling (T-icon colour, assignee chip,
  // reminder pill) and DOM-attribute reads both need the attrs back on
  // the live nodes; the cache is just the in-memory mirror. Each branch
  // is no-op when the live attr is already present, so this runs every
  // editor update without thrashing.
  useEffect(() => {
    if (!enableChecklist) return
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const root = quill.root as HTMLElement
    const items = Array.from(root.querySelectorAll('.ql-checklist-item')) as HTMLElement[]
    items.forEach((item) => {
      const id = item.getAttribute('data-checklist-item-id')
      if (!id) return
      const cached = checklistItemCache.get(id)
      if (!cached) return
      if (cached.task_id && !item.getAttribute('data-checklist-task-id')) {
        item.setAttribute('data-checklist-task-id', cached.task_id)
      }
      if (cached.task_key && !item.getAttribute('data-checklist-task-key')) {
        item.setAttribute('data-checklist-task-key', cached.task_key)
      }
      if (cached.task_status && !item.getAttribute('data-checklist-task-status')) {
        item.setAttribute('data-checklist-task-status', cached.task_status)
      }
      if (cached.task_bpids && !item.getAttribute('data-checklist-task-bpids')) {
        item.setAttribute('data-checklist-task-bpids', cached.task_bpids)
      }
      if (
        cached.assignees &&
        cached.assignees.length > 0 &&
        !item.getAttribute('data-checklist-assignees')
      ) {
        item.setAttribute('data-checklist-assignees', JSON.stringify(cached.assignees))
        // Keep the button chip state aligned with the restored data so the
        // assignee count badge / tooltip don't look empty post-rebuild.
        const btn = item.querySelector<HTMLElement>('.ql-checklist-item-assignee')
        if (btn) {
          btn.setAttribute('data-has-assignees', 'true')
          btn.setAttribute('data-assignee-count', String(cached.assignees.length))
        }
      }
      if (cached.due_date && !item.getAttribute('data-checklist-due-date')) {
        item.setAttribute('data-checklist-due-date', cached.due_date)
      }
      if (cached.due_time && !item.getAttribute('data-checklist-due-time')) {
        item.setAttribute('data-checklist-due-time', cached.due_time)
      }
      if (cached.due_date || cached.due_time) {
        applyChecklistDueColor(item)
      }
    })
  }, [editor, enableChecklist])

  // Per-item action icons (assignee, reminder, T) are shown when the parent
  // is a SAVED task OR a SAVED note — both can create tasks from checklist
  // items (inheriting the item's assignees / due date) via the same modal.
  // Hidden otherwise, which covers:
  //   - new task/page modals (no id yet)
  //   - quick notes / homepage / design board (no create-task callback)
  // The trash icon stays so the user can still delete checklist items.
  // Same signal as the T-button gate above: host passed the create-task
  // callback AND the entity is persisted.
  useEffect(() => {
    if (!enableChecklist) return
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const root = quill.root as HTMLElement
    const canCreateTasks =
      !!onCreateTaskFromChecklistItem && !!currentCardOrPage?.id
    if (canCreateTasks) {
      root.classList.remove('ql-checklist-no-task-actions')
    } else {
      root.classList.add('ql-checklist-no-task-actions')
    }
  }, [
    enableChecklist,
    onCreateTaskFromChecklistItem,
    currentCardOrPage?.id,
  ])

  // Confirm handler for the create-task-from-checklist modal. Lives here (not
  // in the modal component) because it needs the live Quill ref + the cache-
  // aware DOM helpers to stamp the task link back onto the checklist item.
  // NOTE: we deliberately do NOT call handleFormSubmission — auto-saving the
  // parent before the attrs are stamped would persist a link-less description.
  const handleConfirmCreateTask = async () => {
    const task = taskFromChecklist
    if (!task || !onCreateTaskFromChecklistItem) return
    const title = task.text.trim()
    if (!title) return
    setTaskFromChecklistSaving(true)
    setTaskFromChecklistError(null)
    try {
      // Prefer the snapshot taken at modal open (stable against Quill
      // rebuilds between open and confirm), but fall back to a fresh DOM
      // read keyed off the item id when the snapshot is empty — that catches
      // rebuild races where the snapshot ran before the attrs were stamped.
      let finalAssignees = task.assignees
      let finalDueDate = task.due_date
      let finalDueTime = task.due_time
      const quillNow = quillRef.current?.getEditor()
      const liveItem = quillNow?.root.querySelector(
        `[data-checklist-item-id="${task.itemId}"]`
      ) as HTMLElement | null
      if (liveItem) {
        const liveAssignees = readChecklistAssignees(liveItem) as unknown as ChecklistAssigneeDropdownType[]
        if (liveAssignees.length > finalAssignees.length) {
          finalAssignees = liveAssignees
        }
        const liveDue = readChecklistDueDateRaw(liveItem)
        if (!finalDueDate && liveDue.due_date) {
          finalDueDate = liveDue.due_date
          finalDueTime = liveDue.due_time
        }
      }
      // Resolve the target column. In "new" mode we create the column first
      // (it lands as the last column on the kanban) and drop the new task
      // into it; otherwise we use the picked column. Falls back to undefined
      // so the task hook inherits the parent card's column for legacy callers.
      let targetColumnId: string | null | undefined = columnChoice.columnId || undefined
      if (columnChoice.mode === 'new' && onCreateColumnFromChecklist) {
        // Creating a board column triggers a board refetch. Persist the open
        // card first (PATCH) so any unsaved edits — including the checklist
        // we're acting on — aren't lost when the board reloads. Card-only:
        // saveCardDescription is card-shaped; a note (has `title`) saves via
        // its own flow.
        if (
          currentCardOrPage?.id &&
          currentCardOrPage?.boardId &&
          !currentCardOrPage?.title
        ) {
          const currentHtml =
            quillRef.current?.getEditor()?.root.innerHTML ?? editor ?? null
          await saveCardDescription(currentCardOrPage, currentHtml)
        }
        const name = columnChoice.newName.trim() || CHECKLIST_COLUMN_DEFAULT_NAME
        const newCol = await onCreateColumnFromChecklist({
          name,
          color: columnChoice.newColor || CHECKLIST_COLUMN_DEFAULT_COLOR,
          columns: columns ?? [],
        })
        targetColumnId = newCol?.id ?? targetColumnId
      }
      const result = await onCreateTaskFromChecklistItem({
        title,
        assignees: finalAssignees as unknown as ChecklistAssignee[],
        due_date: finalDueDate,
        due_time: finalDueTime,
        columnId: targetColumnId,
      })
      // Remember the column for this board so the next checklist task on the
      // same board defaults to it.
      setLastChecklistColumn(boardId || currentCardOrPage?.boardId, targetColumnId)
      // Persist the link on the checklist item DOM + update the item text so
      // it matches what the user saved, then sync Quill. Re-query after the
      // await so we get whichever DOM node is live now.
      if (result?.id && result?.key) {
        const quillAfter = quillRef.current?.getEditor()
        const item = quillAfter?.root.querySelector(
          `[data-checklist-item-id="${task.itemId}"]`
        ) as HTMLElement | null
        if (item) {
          // Cache-aware writes so the task-link attrs survive the setEditor →
          // clipboard.convert round-trip that follows; otherwise the next T
          // click would re-open Create instead of previewing the new task.
          writeChecklistTaskAttr(item, 'task_id', 'data-checklist-task-id', result.id)
          writeChecklistTaskAttr(item, 'task_key', 'data-checklist-task-key', result.key)
          writeChecklistTaskAttr(item, 'task_status', 'data-checklist-task-status', 'todo')
          if (Array.isArray(result.assignees) && result.assignees.length > 0) {
            writeChecklistTaskAttr(
              item,
              'task_bpids',
              'data-checklist-task-bpids',
              JSON.stringify(result.assignees)
            )
          }
          const textEl = item.querySelector<HTMLElement>('.ql-checklist-item-text')
          if (textEl) textEl.textContent = title
        }
        if (quillAfter) {
          const newHtml = quillAfter.root.innerHTML
          setEditor(newHtml)
          // saveCardDescription is card-shaped (card_name, board_name…) so
          // only fire it for cards — a note (page, identified by `title`)
          // would be mis-saved. Notes persist the link via their own next
          // save of the updated editor content set above.
          if (
            currentCardOrPage?.id &&
            currentCardOrPage?.boardId &&
            !currentCardOrPage?.title
          ) {
            saveCardDescription(currentCardOrPage, newHtml)
          }
        }
      }
      setTaskFromChecklist(null)
    } catch (err: any) {
      setTaskFromChecklistError(
        err?.response?.data?.message || err?.message || 'Failed to create task.'
      )
    } finally {
      setTaskFromChecklistSaving(false)
    }
  }

  // Sync the `data-checklist-task-status` attribute on every linked checklist
  // item with the current task status from redux. CSS paints the "T" icon
  // based on this attr so status changes elsewhere (board drag, status
  // dropdown) propagate live. Purely visual — never writes back to Quill.
  // Same pass also mirrors the task's board assignees onto the assignee
  // button (count chip + tooltip + always-visible state) so the user can
  // see who's assigned without opening the picker.
  useEffect(() => {
    if (!enableChecklist) return
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const items = Array.from(
      quill.root.querySelectorAll('.ql-checklist-item[data-checklist-task-id]')
    ) as HTMLElement[]
    const nameOf = (a: any): string =>
      typeof a === 'string' ? a : (a?.fullName ?? a?.name ?? '')
    // Has either redux slice loaded yet? If both are empty, we can't
    // tell "deleted" from "not yet fetched" — leave linkages alone.
    const reduxLoaded =
      (Array.isArray(allCurrentUserCards) && allCurrentUserCards.length > 0) ||
      (Array.isArray(boardScopedCards) && boardScopedCards.length > 0)
    let unlinkedAny = false
    let checkboxToggledAny = false
    items.forEach((item: HTMLElement) => {
      const taskId = item.getAttribute('data-checklist-task-id')
      const taskKey = item.getAttribute('data-checklist-task-key')
      const findIn = (arr: any) =>
        Array.isArray(arr)
          ? arr.find(
              (c: any) =>
                c?.id === taskId ||
                (typeof c?.key === 'string' && taskKey && c.key.trim() === taskKey)
            )
          : undefined
      const match = findIn(allCurrentUserCards) ?? findIn(boardScopedCards)
      // Linked task no longer exists in either redux slice (and redux has
      // loaded at least once) → treat as deleted. Clear the entire footprint
      // the task left on the checklist item so it reverts to a clean,
      // unlinked state without a page reload: the task link, the reminder
      // (due date), and the assignees.
      if (!match && reduxLoaded) {
        unlinkedAny = true
        // Purge the in-memory mirror FIRST. The cache is read before the DOM
        // by the due-date / assignee readers, so it must be gone before we
        // recompute any visual state below — otherwise applyChecklistDueColor
        // would re-derive the reminder colour from the still-cached due date
        // and the pill would never clear. It also stops the re-stamp effect
        // (which restores DOM attrs from the cache after a Quill rebuild) from
        // re-linking everything we clear here, and lets the item drop out of
        // the `[data-checklist-task-id]` query on the next pass so the unlink
        // settles instead of looping.
        const cacheKey = cacheKeyFor(item)
        if (cacheKey) checklistItemCache.delete(cacheKey)
        // Task link → T icon reverts to its default (grey) style and the
        // click handler falls back to the Create Task modal.
        item.removeAttribute('data-checklist-task-id')
        item.removeAttribute('data-checklist-task-key')
        item.removeAttribute('data-checklist-task-status')
        item.removeAttribute('data-checklist-task-bpids')
        item.removeAttribute('data-checklist-task-assignees')
        // Reminder → clear the due date and reset the clock pill colour.
        item.removeAttribute('data-checklist-due-date')
        item.removeAttribute('data-checklist-due-time')
        applyChecklistDueColor(item)
        // Assignees → clear the picks + badge.
        item.removeAttribute('data-checklist-assignees')
        const btn = item.querySelector<HTMLElement>('.ql-checklist-item-assignee')
        if (btn) {
          btn.removeAttribute('data-has-assignees')
          btn.removeAttribute('data-assignee-count')
          btn.setAttribute('title', 'Assign to')
        }
        return
      }
      const status = match?.status ?? 'todo'
      const prevStatus = item.getAttribute('data-checklist-task-status')
      if (prevStatus !== status) {
        item.setAttribute('data-checklist-task-status', status)
        // Mirror the linked task's Done state onto the checklist item's
        // checkbox. Done → ticked (strikethrough + count up); Done → To Do /
        // In Progress → unticked (count down). Transitions to Archived (and a
        // deleted task, handled in the !match branch above) leave the checkbox
        // untouched. Only fires on an actual task-status change, so a user's
        // manual tick/untick of a linked item is never overridden.
        const node = item.closest<HTMLElement>('.ql-checklist-embed')
        if (status === 'done') {
          if (item.getAttribute('data-checklist-checked') !== 'true') {
            item.setAttribute('data-checklist-checked', 'true')
            if (node) updateChecklistStatus(node)
            checkboxToggledAny = true
          }
        } else if (
          prevStatus === 'done' &&
          (status === 'todo' || status === 'in-progress')
        ) {
          if (item.getAttribute('data-checklist-checked') !== 'false') {
            item.setAttribute('data-checklist-checked', 'false')
            if (node) updateChecklistStatus(node)
            checkboxToggledAny = true
          }
        }
      }
      // Mirror task due_date / due_time onto the item DOM so the clock
      // icon stays in sync (color + tooltip) when the task is edited
      // anywhere else (kanban modal, board drag, etc.). Same UTC string
      // shape we store in writeChecklistDueDate so this is a direct copy.
      const taskDueDate: string | null = match?.due_date ?? null
      const taskDueTime: string | null = match?.due_time ?? null
      const itemDueDate = item.getAttribute('data-checklist-due-date')
      const itemDueTime = item.getAttribute('data-checklist-due-time')
      if (itemDueDate !== taskDueDate || itemDueTime !== taskDueTime) {
        if (taskDueDate && taskDueTime) {
          item.setAttribute('data-checklist-due-date', taskDueDate)
          item.setAttribute('data-checklist-due-time', taskDueTime)
        } else {
          item.removeAttribute('data-checklist-due-date')
          item.removeAttribute('data-checklist-due-time')
        }
        applyChecklistDueColor(item)
        unlinkedAny = true
      }
      // Build the merged assignee view: task board assignees + local
      // contact/freetext entries. Task drives board membership; the local
      // DOM cache holds non-board entries the picker can attach to a
      // linked item.
      const taskAssigneeNames: string[] = Array.isArray(match?.assignees)
        ? match.assignees
            .map((a: any) => nameOf(a).trim())
            .filter((n: string) => n.length > 0)
        : []
      // If redux happens to carry the rich {board_person_id, fullName}
      // shape (true after a PATCH echo from anywhere — kanban card
      // modal, our own checklist sync, etc.), refresh the DOM bpid
      // stamp so the next picker open seeds from authoritative data.
      // Preserves any userId from the existing stamp by bpid lookup so
      // we don't degrade matching reliability when redux's shape lacks
      // userId. Skipped entirely when redux only has the degraded
      // string[] shape.
      if (Array.isArray(match?.assignees) && match.assignees.length > 0) {
        const richAssignees = match.assignees
          .filter(
            (a: any) =>
              a && typeof a === 'object' && typeof a.board_person_id === 'string'
          )
          .map((a: any) => ({
            board_person_id: a.board_person_id as string,
            fullName: (a.fullName ?? a.name ?? '') as string,
            userId: typeof a.userId === 'string' ? (a.userId as string) : undefined,
          }))
        if (richAssignees.length === match.assignees.length) {
          // Recover userId from the previous stamp where redux didn't
          // supply one (most common case — getAllCard / PATCH echoes
          // typically omit userId).
          const existingByBpid: Record<string, string> = {}
          const prevStamp = item.getAttribute('data-checklist-task-bpids')
          if (prevStamp) {
            try {
              const prev = JSON.parse(prevStamp)
              if (Array.isArray(prev)) {
                prev.forEach((p: any) => {
                  if (p?.board_person_id && typeof p?.userId === 'string') {
                    existingByBpid[p.board_person_id] = p.userId
                  }
                })
              }
            } catch {
              /* ignore */
            }
          }
          const merged = richAssignees.map((r: { board_person_id: string; fullName: string; userId?: string }) => ({
            ...r,
            userId: r.userId ?? existingByBpid[r.board_person_id],
          }))
          item.setAttribute(
            'data-checklist-task-bpids',
            JSON.stringify(merged)
          )
        }
      }
      const localAssignees = readChecklistAssignees(item)
      const localNames = localAssignees.map((a) => a.name)
      const allNames = [...taskAssigneeNames, ...localNames]
      // Persist the task-side names on the item so other consumers (CSS,
      // future readers) can react without re-querying redux.
      if (taskAssigneeNames.length > 0) {
        item.setAttribute(
          'data-checklist-task-assignees',
          JSON.stringify(taskAssigneeNames)
        )
      } else {
        item.removeAttribute('data-checklist-task-assignees')
      }
      const btn = item.querySelector<HTMLElement>('.ql-checklist-item-assignee')
      if (btn) {
        if (allNames.length > 0) {
          btn.setAttribute('data-has-assignees', 'true')
          btn.setAttribute('data-assignee-count', String(allNames.length))
          const shown = allNames.slice(0, 5).join(', ')
          const extra = allNames.length > 5 ? ` +${allNames.length - 5} more` : ''
          btn.setAttribute('title', `Assigned to: ${shown}${extra}`)
        } else {
          btn.removeAttribute('data-has-assignees')
          btn.removeAttribute('data-assignee-count')
          btn.setAttribute('title', 'Assign to')
        }
      }
    })
    // Persist stripped state so the un-linked items survive a save.
    // Safe re-run guard: subsequent passes find nothing to strip
    // (attributes already gone) so this won't loop on the `editor` dep.
    if (unlinkedAny || checkboxToggledAny) {
      setEditor(quill.root.innerHTML)
    }
  }, [allCurrentUserCards, boardScopedCards, editor, enableChecklist])

  // Confirm modal handlers
  const confirmDeleteChecklist = () => {
    const embed = pendingChecklistDeleteRef.current
    const quill = quillRef.current?.getEditor()
    if (embed && quill) {
      // ChecklistEmbed.deleteAt() is overridden to block stray deletes, so
      // quill.deleteText() would be a no-op here. Detach the blot directly
      // via Parchment, then sync Quill's internal model.
      const blot = (Quill as any).find(embed)
      if (blot && typeof blot.remove === 'function') {
        blot.remove()
      } else {
        embed.remove()
      }
      quill.update('user')
      setEditor(quill.root.innerHTML)
    }
    pendingChecklistDeleteRef.current = null
    setShowChecklistDeleteConfirm(false)
  }

  const cancelDeleteChecklist = () => {
    pendingChecklistDeleteRef.current = null
    setShowChecklistDeleteConfirm(false)
  }

  const confirmDeleteChecklistItem = () => {
    const pending = itemDeleteConfirm
    const quill = quillRef.current?.getEditor()
    if (pending && quill) {
      const item = quill.root.querySelector(
        `[data-checklist-item-id="${pending.itemId}"]`
      ) as HTMLElement | null
      const node = item?.closest<HTMLElement>('.ql-checklist-embed') ?? null
      if (item && node) {
        // Only the checklist item is removed — the task created from it is
        // intentionally left in place (per requirements).
        removeChecklistItem(node, item)
        setEditor(quill.root.innerHTML)
      }
    }
    setItemDeleteConfirm(null)
  }

  const cancelDeleteChecklistItem = () => setItemDeleteConfirm(null)

  // Board members passed to the assignee dropdown. Prefer the richer
  // boardPersonsForSendAlert (has email) and fall back to optionsForSendAlert
  // (label + value only) so the picker still works when the parent form
  // didn't pass the full shape.
  const checklistBoardMembers = useMemo<ChecklistBoardMember[]>(() => {
    const full = boardPersonsForSendAlertRef.current
    if (Array.isArray(full) && full.length > 0) {
      return full.map((p: any) => ({
        id: p.id,
        name: p.name,
        email: p.email,
        isDeleted: p.isDeleted,
        // boardPersonId (when available) lets the dropdown stamp the bpid
        // on the picked assignee so create-task-from-checklist can build
        // card_persons rows without re-fetching the roster.
        boardPersonId: p.boardPersonId,
      }))
    }
    const opts = optionsForSendAlertRef.current
    if (Array.isArray(opts)) {
      return opts.map((o: any) => ({
        id: o.value,
        name: o.label,
        isDeleted: o.isDisabled,
      }))
    }
    return []
    // Recomputed whenever the picker opens via the `assigneePicker` dep so we
    // always get a fresh snapshot at the moment the dropdown mounts.
  }, [assigneePicker, boardPersonsForSendAlert, optionsForSendAlert])

  // Notify newly assigned parties on a checklist item:
  //  - Board member → push alert (has subscriberId + email; Novu workflow
  //    "checklist-item-assigned" should be configured on the backend)
  //  - Contact with email → same event, email only (no subscriberId). The
  //    backend decides: if the email is a registered b4igo user with an
  //    active license they get a notification; otherwise it no-ops.
  //  - Contact without email → silent no-op
  //  - Freetext assignee → silent no-op (no way to reach them)
  // All calls are wrapped so failures never break the editor UI — the feature
  // is best-effort; missing backend config is a soft-fail.
  const sendChecklistAssignmentAlerts = useCallback(
    async (item: HTMLElement, newlyAdded: ChecklistAssigneeDropdownType[]) => {
      if (newlyAdded.length === 0) return
      const embed = item.closest<HTMLElement>('.ql-checklist-embed')
      const heading = embed?.querySelector<HTMLElement>('.ql-checklist-heading-title')
      const checklistTitle = (heading?.textContent ?? '').trim() || 'Checklist'
      const itemText = (item.querySelector<HTMLElement>('.ql-checklist-item-text')?.textContent ?? '').trim()

      const payloadBase = {
        assignerName: capitalizeEachWord(fullName),
        checklistTitle,
        itemText,
        boardId,
      }

      for (const a of newlyAdded) {
        if (a.source === 'board') {
          const full = (boardPersonsForSendAlertRef.current || []).find((p: any) => p.id === a.id)
          const email = a.email || full?.email
          if (!email) continue
          try {
            await sendPushAlert({
              eventName: 'checklist-item-assigned',
              subscriberEmail: email,
              subscriberId: a.id,
              payload: payloadBase,
            })
          } catch {
            // swallow
          }
        } else if (a.source === 'contact' && a.email) {
          try {
            await sendPushAlert({
              eventName: 'checklist-item-assigned',
              subscriberEmail: a.email,
              // no subscriberId — backend resolves by email against the
              // license table; unknown email → no-op.
              payload: payloadBase,
            })
          } catch {
            // swallow
          }
        }
        // source === 'freetext' or contact-without-email → nothing to send
      }
    },
    [boardId, fullName]
  )

  const handleAssigneePickerClose = (newAssignees: ChecklistAssigneeDropdownType[]) => {
    if (!assigneePicker) return
    const { item, initial } = assigneePicker
    // Remember when/which item just closed so an immediate re-open attempt
    // (from the `click` after the dismiss `mousedown`) is suppressed.
    const itemId = item.getAttribute('data-checklist-item-id') ?? ''
    lastAssigneeCloseRef.current = { itemId, at: Date.now() }
    // Diff: anything in new that's not in initial → newly added
    const keyOf = (a: ChecklistAssigneeDropdownType) => `${a.source}:${a.id}`
    const oldKeys = new Set(initial.map(keyOf))
    const newlyAdded = newAssignees.filter((a) => !oldKeys.has(keyOf(a)))
    const taskId = item.getAttribute('data-checklist-task-id')
    if (taskId) {
      // Linked item: board assignees are owned by the task. Persist only
      // the local (contact/freetext) entries on the checklist; push the
      // board list to the task via updateCardById.
      const nonBoard = newAssignees.filter((a) => a.source !== 'board')
      const boardOnly = newAssignees.filter((a) => a.source === 'board')
      const initialBoardOnly = initial.filter((a) => a.source === 'board')
      writeChecklistAssignees(item, nonBoard as unknown as ChecklistAssignee[])
      // Optimistic icon update — the live-sync useEffect will repaint
      // from redux once the PATCH echo lands, but Done should feel
      // immediate, not lag behind a network round trip.
      const allNames = newAssignees.map((a) => a.name).filter((n) => n.trim().length > 0)
      const btn = item.querySelector<HTMLElement>('.ql-checklist-item-assignee')
      if (btn) {
        if (allNames.length > 0) {
          btn.setAttribute('data-has-assignees', 'true')
          btn.setAttribute('data-assignee-count', String(allNames.length))
          const shown = allNames.slice(0, 5).join(', ')
          const extra = allNames.length > 5 ? ` +${allNames.length - 5} more` : ''
          btn.setAttribute('title', `Assigned to: ${shown}${extra}`)
        } else {
          btn.removeAttribute('data-has-assignees')
          btn.removeAttribute('data-assignee-count')
          btn.setAttribute('title', 'Assign to')
        }
      }
      const quill = quillRef.current?.getEditor()
      if (quill) setEditor(quill.root.innerHTML)
      setAssigneePicker(null)
      sendChecklistAssignmentAlerts(item, newlyAdded)
      // Refresh the DOM bpid stamp from the PATCH echo so the next
      // picker open is seeded from a fresh, authoritative source rather
      // than redux's degraded string-of-fullNames shape. Re-query the
      // item by id INSIDE the .then() — the early setEditor above plus
      // the redux dispatches inside the sync may have triggered a Quill
      // rebuild that detaches our captured node, so writing attributes
      // on the closure reference would never reach the live editor.
      const itemIdForRestamp = item.getAttribute('data-checklist-item-id') ?? ''
      Promise.resolve(syncAssigneesToTask(taskId, boardOnly, initialBoardOnly))
        .then((finalAssignees) => {
          if (!Array.isArray(finalAssignees)) return
          const quillNow = quillRef.current?.getEditor()
          if (!quillNow || !itemIdForRestamp) return
          const liveItem = quillNow.root.querySelector(
            `[data-checklist-item-id="${itemIdForRestamp}"]`
          ) as HTMLElement | null
          if (!liveItem) return
          if (finalAssignees.length > 0) {
            liveItem.setAttribute(
              'data-checklist-task-bpids',
              JSON.stringify(finalAssignees)
            )
          } else {
            liveItem.removeAttribute('data-checklist-task-bpids')
          }
          setEditor(quillNow.root.innerHTML)
        })
        .catch(() => {
          /* sync hook swallows its own errors */
        })
    } else {
      writeChecklistAssignees(item, newAssignees as unknown as ChecklistAssignee[])
      const quill = quillRef.current?.getEditor()
      if (quill) setEditor(quill.root.innerHTML)
      setAssigneePicker(null)
      sendChecklistAssignmentAlerts(item, newlyAdded)
    }
  }

  // Task ↔ checklist assignee sync and freetext → contact upgrade live in
  // their own hooks; see ../hooks/useTaskAssigneeSync and
  // ../hooks/useUpgradeFreeTextAssignee.
  const pushAssigneesToTask = useTaskAssigneeSync()
  const pushDueDateToTask = useTaskDueDateSync()
  const saveCardDescription = useSaveCardDescription()
  const syncAssigneesToTask = (
    taskId: string,
    boardAssignees: ChecklistAssigneeDropdownType[],
    initialBoardAssignees: ChecklistAssigneeDropdownType[]
  ) => {
    // Look in both slices — same pattern as the status-sync effect above.
    // allCurrentUserCards only has tasks the current user is assigned to,
    // so a checklist linking to a board-mate's task would silently no-op
    // here unless we also consult boardScopedCards.
    const match =
      allCurrentUserCardsRef.current?.find((c: any) => c?.id === taskId) ??
      boardScopedCardsRef.current?.find((c: any) => c?.id === taskId)
    if (!match) return
    return pushAssigneesToTask(taskId, boardAssignees, initialBoardAssignees, match)
  }
  const upgradeFreeTextToContact = useUpgradeFreeTextAssignee({
    getEditorRoot: () => (quillRef.current?.getEditor()?.root as HTMLElement) ?? null,
    setEditor: (html) => setEditor(html),
    sendAlerts: (item, added) =>
      sendChecklistAssignmentAlerts(
        item,
        added as unknown as ChecklistAssigneeDropdownType[]
      ),
  })

  // Inline "+" key detection — shows the dropdown at the caret.
  // We listen to the editor's text-change so we catch the "+" regardless of
  // whether it arrived via keydown, IME, or paste of a single char.
  useEffect(() => {
    if (!enableChecklist) return
    const quill = quillRef.current?.getEditor()
    if (!quill) return

    // Index (in the new document) of a "+" inserted by this change, or -1.
    // Reading it from the delta — not the live selection — is what makes the
    // trigger survive a paste: the custom paste handler inserts via the 'api'
    // source and then repositions the caret asynchronously, so by the time a
    // selection-based check runs the caret is no longer just after the "+".
    const insertedPlusIndex = (delta: any): number => {
      if (!Array.isArray(delta?.ops)) return -1
      let i = 0
      let found = -1
      for (const op of delta.ops) {
        if (typeof op.retain === 'number') i += op.retain
        else if (typeof op.insert === 'string') {
          const at = op.insert.lastIndexOf('+')
          if (at !== -1) found = i + at
          i += op.insert.length
        } else if (op.insert != null) i += 1 // embed
        // delete ops don't advance the new-document pointer
      }
      return found
    }

    const checkForPlusAt = (plusIndex: number) => {
      // The "+" must be at the start of a line or preceded by whitespace —
      // inside existing words we don't want the dropdown (e.g. C++ code).
      if (plusIndex >= 1) {
        const before = quill.getText(plusIndex - 1, 1)
        if (before && !/\s/.test(before) && before !== '\n') return
      }
      // Don't show the dropdown if the "+" is inside a checklist or table embed
      const active = document.activeElement as HTMLElement | null
      if (active?.closest?.('.ql-checklist-embed') || active?.closest?.('.ql-table-embed')) return
      // Position in viewport coordinates and render position:fixed (see JSX).
      // The "+" lives inside a nested scroller, so pinning the dropdown to an
      // offset parent (position:absolute) can't track it — they don't share a
      // scroll context. Fixed + viewport coords has no offset-parent dependency;
      // the capture-phase scroll listener recomputes this point on every scroll.
      // quill.getBounds() is relative to .ql-container, so add that element's
      // viewport rect to get an absolute viewport position.
      const bounds = quill.getBounds(plusIndex + 1)
      const qlContainerRect = (quill.container as HTMLElement).getBoundingClientRect()
      // Close once the "+" scrolls out of the editor's visible box (bounds are
      // relative to .ql-container) — otherwise the fixed dropdown would keep
      // floating over the toolbar/title above the editor.
      if (bounds.bottom < 0 || bounds.top > qlContainerRect.height) {
        setChecklistDropdown(null)
        return
      }
      setChecklistDropdown({
        top: qlContainerRect.top + bounds.bottom + 4,
        left: qlContainerRect.left + bounds.left,
        plusIndex,
      })
    }

    const onTextChange = (delta: any, _old: any, source: string) => {
      // Close the dropdown the moment its anchoring "+" is deleted/overwritten.
      // Backspacing the "+" fires text-change (not selection-change), so the
      // selection-change closer below never runs for it.
      if (checklistDropdown && quill.getText(checklistDropdown.plusIndex, 1) !== '+') {
        setChecklistDropdown(null)
      }
      if (source === 'user') {
        // Typing: the "+" is always the character just before the live caret.
        // Anchor to that, never to some earlier "+" elsewhere in the document.
        const sel = quill.getSelection()
        if (!sel || quill.getText(sel.index - 1, 1) !== '+') return
        const plusIndex = sel.index - 1
        setTimeout(() => checkForPlusAt(plusIndex), 0)
        return
      }
      // Paste / programmatic insert: the caret isn't reliably after the "+", so
      // locate it from the delta. Only when the pasted content is exactly "+",
      // so pasting prose that happens to contain " + " doesn't pop the menu.
      const plusIndex = insertedPlusIndex(delta)
      if (plusIndex === -1) return
      const inserted = delta.ops
        .map((o: any) => (typeof o.insert === 'string' ? o.insert : ''))
        .join('')
      if (inserted.trim() !== '+') return
      setTimeout(() => checkForPlusAt(plusIndex), 0)
    }
    const onSelectionChange = () => {
      // If the cursor moved away from the "+" position, close the dropdown
      if (!checklistDropdown) return
      const sel = quill.getSelection()
      if (!sel) return
      if (sel.index - 1 !== checklistDropdown.plusIndex) {
        setChecklistDropdown(null)
      }
    }

    // Keep the dropdown glued to its "+" while anything scrolls — the editor's
    // own scroller, the modal body, or the page. Scroll events don't bubble, so
    // a capture-phase listener on document is the only way to catch every
    // ancestor scroller (the editor auto-grows and a parent owns the scrollbar).
    const reposition = () => {
      if (checklistDropdown) checkForPlusAt(checklistDropdown.plusIndex)
    }
    document.addEventListener('scroll', reposition, { capture: true, passive: true })

    quill.on('text-change', onTextChange)
    quill.on('selection-change', onSelectionChange)
    return () => {
      document.removeEventListener('scroll', reposition, { capture: true } as any)
      quill.off('text-change', onTextChange)
      quill.off('selection-change', onSelectionChange)
    }
  }, [checklistDropdown, enableChecklist])

  // Hold the view still across an edit. The editor is controlled, so react-quill
  // re-runs setContents on our fed-back value and resets the scroll to the top;
  // the auto-grow layout means a parent owns the scrollbar. One rule covers both:
  // capture the scroll position before the reconcile, then on the next frame keep
  // it exactly there IF the caret is still visible (typing mid-note, a heading
  // change, etc. — the view must not move), and only scroll when the caret has
  // actually dropped off-screen (Enter/newline pushing it past the fold).
  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    if (!quill) return
    const keepCaretVisible = (delta?: any) => {
      // A formatting-only change (heading level, bold, etc.) carries only
      // `retain` ops. For block-format changes the scroll is reset earlier —
      // Quill's toolbar calls quill.focus() (scrolls .ql-editor to the top)
      // before the format even applies — so capturing here reads the
      // already-lost position (0). Scroll preservation for those is handled at
      // the toolbar handler instead (modules.toolbar.handlers.header, restoring
      // the toolbar-mousedown snapshot). Nothing to do here.
      const ops = delta?.ops
      const isFormatOnly =
        Array.isArray(ops) &&
        ops.length > 0 &&
        !ops.some((op: any) => op.insert != null || op.delete != null)
      if (isFormatOnly) return

      // Only manage caret-following while the user is actually editing this
      // editor — skip background/initial content loads.
      if (!quill.hasFocus()) return
      const scrollContainer = getScrollableAncestor(quill.root) || quill.root
      // Captured BEFORE react-quill's reconcile runs, so it's the pre-edit position.
      const prevScrollTop = scrollContainer.scrollTop

      requestAnimationFrame(() => {
        // Restore the pre-edit scroll FIRST — this undoes react-quill's spurious
        // reset-to-top. Only then is it meaningful to ask whether the caret is
        // visible, because visibility must be judged against where the user
        // actually was, not the position react-quill jumped to. (If we measured
        // before restoring, an edit that reset the scroll would make the caret
        // look off-screen and we'd wrongly "follow" it to the bottom edge.)
        scrollContainer.scrollTop = prevScrollTop

        // A checkbox toggle isn't a text edit — keep the view exactly where it
        // was (already restored above) rather than chasing the stale caret.
        if (Date.now() - lastChecklistToggleAt < 600) return

        const domSel = window.getSelection()
        if (!domSel || domSel.rangeCount === 0) return
        const range = domSel.getRangeAt(0)
        let node: Node = range.startContainer
        // At a block boundary (e.g. caret after a pasted image) startContainer
        // is the editor root + an offset — resolve the child the caret sits on.
        if (node.nodeType !== Node.TEXT_NODE) {
          node = node.childNodes[range.startOffset] || node.childNodes[range.startOffset - 1] || node
        }
        const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : (node as HTMLElement)
        if (!el || !quill.root.contains(el)) return
        const containerRect = scrollContainer.getBoundingClientRect()
        const elRect = el.getBoundingClientRect()
        const caretVisible = elRect.top >= containerRect.top && elRect.bottom <= containerRect.bottom
        // Caret is off-screen relative to the restored position only in the
        // genuine case (e.g. Enter/newline pushed it past the fold) — follow it.
        // Otherwise the view already stayed exactly put.
        if (!caretVisible) {
          el.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
        }
      })
    }
    quill.on('text-change', keepCaretVisible)
    return () => quill.off('text-change', keepCaretVisible)
  }, [])

  // Tab / Enter on the dropdown confirms the checklist; Escape dismisses
  useEffect(() => {
    if (!checklistDropdown) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        const idx = checklistDropdown.plusIndex
        setChecklistDropdown(null)
        insertChecklistReplacingPlus(idx)
      } else if (e.key === 'Escape') {
        setChecklistDropdown(null)
      }
    }
    // capture phase so we beat Quill's own Tab handler
    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [checklistDropdown])

  const formats = useMemo(
    () => [
      'header',
      'font',
      'size',
      'bold',
      'italic',
      'underline',
      'align',
      'strike',
      'script',
      'blockquote',
      'background',
      'list',
      'bullet',
      'indent',
      'link',
      'image',
      'video',
      'color',
      'code-block',
      'mention',
      'tableEmbed',
      'checklistEmbed',
    ],
    []
  )

  function CardData({ data }: { data: string }): React.ReactElement {
    return <span dangerouslySetInnerHTML={createSafeHtml(data, 'default')} />
  }
  async function suggestTask(searchTerm: any) {
    const filteredCards = allCurrentUserCards?.filter((c: any) => c.boardId === boardId)

    const pickName = (card: any): string => {
      const candidate =
        card?.card_name ?? card?.cardName ?? card?.title ?? card?.name ?? ''
      return typeof candidate === 'string' ? candidate.trim() : ''
    }
    const cardIds =
      filteredCards?.length > 0 &&
      filteredCards?.map((card: any) => ({
        id: `${card?.key.trim()}`,
        value: `${card?.key.trim()}`,
        title: pickName(card),
      }))

    cardIds?.length > 0 &&
      cardIds?.sort((a: any, b: any) => {
        return a.value.localeCompare(b.value)
      })

    if (!(cardIds?.length > 0)) return []
    const term = String(searchTerm || '').toLowerCase()
    if (!term) return cardIds
    return cardIds.filter((c: any) =>
      c.value.toLowerCase().includes(term) ||
      (c.title && c.title.toLowerCase().includes(term))
    )
  }
  async function suggestPages(searchTerm: any) {
    // Resolve the board from the prop or, on the homepage, from the open page/card.
    const effectiveBoardId = boardIdRef.current || currentCardOrPageRef.current?.boardId
    // Fetch the board's pages on demand (only when `#` is actually used) and cache
    // per board. This is the board-scoped source — not the user-wide
    // `pagesByLoggedInUser` snapshot, which can be stale or from a previous board.
    let boardPages: any[] = []
    if (effectiveBoardId) {
      if (boardPagesCacheRef.current[effectiveBoardId]) {
        boardPages = boardPagesCacheRef.current[effectiveBoardId]
      } else {
        try {
          const res = await getAllPagesByBoardId(effectiveBoardId)
          boardPages = res?.data?.data?.pages || []
          boardPagesCacheRef.current[effectiveBoardId] = boardPages
        } catch {
          boardPages = []
        }
      }
    } else {
      // No board context at all — fall back to the user-wide list.
      boardPages = pagesRef.current || []
    }

  const filteredPages = boardPages?.filter((p: any) =>
      (!effectiveBoardId || p.boardId === effectiveBoardId) &&
      p.board_people?.find((bp: any) => bp.user.fullName === fullNameRef.current) &&
      p.key &&
      !p.key.includes("undefined")
    );

    const pageIds = filteredPages
      ?.filter((page: any) => page?.key)
      ?.map((page: any) => ({
        id: page.id,
        value: page.key.trim(),
        title: typeof page?.title === 'string' ? page.title.trim() : '',
      }));


    pageIds && pageIds?.length > 0 &&
      pageIds?.sort((a: any, b: any) => {
        return a.value.localeCompare(b.value)
      })

    if (!(pageIds?.length > 0)) return []
    const term = String(searchTerm || '').toLowerCase()
    if (!term) return pageIds
    return pageIds.filter((p: any) =>
      p.value.toLowerCase().includes(term) ||
      (p.title && p.title.toLowerCase().includes(term))
    )
  }
  async function suggestAlertOptions(searchTerm: any) {
    const options = optionsForSendAlertRef.current
      ?.map((option: any) => ({
        id: option.value,
        value: option.label,
      }));

    options?.length > 0 &&
      options?.sort((a: any, b: any) => {
        return a.value.localeCompare(b.value)
      })
    return options?.length > 0 && options?.filter((option: any) => option.value.includes(searchTerm))
  }
  // Handlers for custom buttons
  const handleCustomMentionTrigger = (char: '@ ' | '/ ' | '# ') => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = quill.getSelection();
    if (!range) return;

    quill.focus(); // Ensure the editor is active

    // ✅ Insert the mention character and move the cursor forward
    quill.insertText(range.index, char);
    quill.setSelection(range.index + 1);

    // ✅ Simulate a real keypress event
    setTimeout(() => {
      const keyMap: Record<string, number> = {
        '@': 50, // KeyCode for '@'
        '/': 191, // KeyCode for '/'
        '#': 51, // KeyCode for '#'
      };

      const event = new KeyboardEvent('keydown', {
        key: char,
        code: `Digit${keyMap[char]}`,
        keyCode: keyMap[char],
        which: keyMap[char],
        bubbles: true,
      });

      quill.root.dispatchEvent(event);
    }, 100);
  };

  // Emoji picker handlers
  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    // Use saved cursor position or get current selection
    let range = savedCursorPosition.current || quill.getSelection();

    // If no selection (cursor not in editor), insert at the end
    if (!range) {
      const length = quill.getLength();
      range = { index: length - 1, length: 0 };
    }

    quill.insertText(range.index, emojiData.emoji);
    quill.setSelection(range.index + emojiData.emoji.length);

    const newContent = quill.root.innerHTML;
    setEditor(newContent);
    setShowEmojiPicker(false);

    // Clear saved position
    savedCursorPosition.current = null;
  };

  const handleEmojiButtonClick = () => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      // Save current cursor position before opening picker
      savedCursorPosition.current = quill.getSelection();
    }
    setShowEmojiPicker(!showEmojiPicker);
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target as Node)) {
        const emojiButton = document.querySelector('.ql-customButtonEmoji');
        if (emojiButton && !emojiButton.contains(event.target as Node)) {
          setShowEmojiPicker(false);
        }
      }
    };

    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEmojiPicker]);

  // Handle highlight color picker button click
  const handleHighlightButtonClick = () => {
    // If we have a table cell selection, keep it (don't overwrite with Quill selection)
    if (!tableCellSelectionRef.current) {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        savedCursorPosition.current = quill.getSelection();
      }
    }
    setShowHighlightPicker(!showHighlightPicker);
  };

  // Apply highlight color to selected text
  const applyHighlightColor = (color: string, closePicker = true) => {
    // If selection is inside a table cell, use native execCommand
    if (applyTableCellFormat('backColor', color)) {
      setHighlightColor(color);
      if (closePicker) {
        setShowHighlightPicker(false);
      }
      return;
    }

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = savedCursorPosition.current || quill.getSelection();
    if (range && range.length > 0) {
      quill.formatText(range.index, range.length, 'background', color);
      setEditor(quill.root.innerHTML);
    }
    setHighlightColor(color);
    if (closePicker) {
      setShowHighlightPicker(false);
      savedCursorPosition.current = null;
    }
  };

  // Close highlight picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (highlightPickerRef.current && !highlightPickerRef.current.contains(event.target as Node)) {
        const highlightButton = document.querySelector('.ql-customButtonHighlight');
        if (highlightButton && !highlightButton.contains(event.target as Node)) {
          setShowHighlightPicker(false);
        }
      }
    };

    if (showHighlightPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showHighlightPicker]);

  // Handle text color picker button click
  const handleTextColorButtonClick = () => {
    // If we have a table cell selection, keep it (don't overwrite with Quill selection)
    if (!tableCellSelectionRef.current) {
      const quill = quillRef.current?.getEditor();
      if (quill) {
        savedCursorPosition.current = quill.getSelection();
      }
    }
    setShowTextColorPicker(!showTextColorPicker);
  };

  // Apply text color to selected text
  const applyTextColor = (color: string, closePicker = true) => {
    // If selection is inside a table cell, use native execCommand
    if (applyTableCellFormat('foreColor', color)) {
      setTextColor(color);
      if (closePicker) {
        setShowTextColorPicker(false);
      }
      return;
    }

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const range = savedCursorPosition.current || quill.getSelection();
    if (range && range.length > 0) {
      quill.formatText(range.index, range.length, 'color', color);
      setEditor(quill.root.innerHTML);
    }
    setTextColor(color);
    if (closePicker) {
      setShowTextColorPicker(false);
      savedCursorPosition.current = null;
    }
  };

  // Close text color picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (textColorPickerRef.current && !textColorPickerRef.current.contains(event.target as Node)) {
        const textColorButton = document.querySelector('.ql-customButtonTextColor');
        if (textColorButton && !textColorButton.contains(event.target as Node)) {
          setShowTextColorPicker(false);
        }
      }
    };

    if (showTextColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTextColorPicker]);

  const modules = useMemo(
    () => ({
      // Disable Quill's "matchVisual" clipboard heuristic. On every
      // setContents round-trip (save → reopen) it inspects the rendered
      // layout and inserts blank lines to "preserve" the vertical gap it
      // sees between block elements. The checklist embed carries a 12px
      // top/bottom margin, so an adjacent paragraph crosses matchSpacing's
      // 1.5× threshold and an empty line keeps getting injected above the
      // checklist — growing on each reopen. Turning it off makes the
      // round-trip idempotent. (Layout-dependent, which is why it never
      // reproduces in a headless/jsdom round-trip.)
      clipboard: { matchVisual: false },
      toolbar:
      {
        container:
          [
            [{ header: [1, 2, false] }],
            [{ 'size': ['small',false, 'large'] }],
            ['bold', 'italic', 'underline', 'strike'], // toggled buttons
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ align: [] }],
            ['customButtonTextColor', 'customButtonHighlight'], // custom text color picker + custom highlight picker
            // [{ font: [] }],
            ['image'],
            ['video'],
            ['link'],
            ...(boardId
              ? [
                ['customButtonMention'],
                ['customButtonLink'],
                ['customButtonPage'],
              ]
              : []),
              ['customButtonEmoji'],
              ['customButtonTable'],
              ...(enableChecklist ? [['customButtonChecklist']] : []),
          ],
        handlers: {
          // Override the default header picker to hold the scroll position.
          // Quill's toolbar calls quill.focus() BEFORE this handler runs, which
          // re-focuses .ql-editor and scrolls it to the top — so we can't
          // capture the position here. Instead we snapshot it on the toolbar
          // mousedown (see the effect below), and just restore that snapshot
          // after the format applies.
          header: (value: any) => {
            const q = quillRef.current?.getEditor()
            if (!q) return
            q.format('header', value || false, 'user')
            restoreScrollPins(toolbarScrollSnapshotRef.current)
          },
          customButtonMention: () => handleCustomMentionTrigger('@ '),
          customButtonLink: () => handleCustomMentionTrigger('/ '),
          customButtonPage: () => handleCustomMentionTrigger('# '),
          customButtonEmoji: handleEmojiButtonClick,
          customButtonHighlight: handleHighlightButtonClick,
          customButtonTextColor: handleTextColorButtonClick,
          customButtonTable: handleTableButtonClick,
          ...(enableChecklist ? { customButtonChecklist: () => insertChecklistAtCursor() } : {}),
        },
      },

      mention: {
        // The description/comment editors scroll at the .ql-container level (the
        // editor itself grows). The default 'normal' strategy mounts the list as
        // an absolute child of .ql-container, whose origin is the unscrolled
        // content top — so once scrolled it lands scrollTop px above the caret.
        // 'fixed' mounts the list on document.body with viewport coords instead.
        positioningStrategy: 'fixed',
        mentionContainerClass: `ql-mention-list-container ${theme === 'dark' ? ' theme__div' : ''}`,
        mentionListClass: theme === 'dark' ? 'theme__div' : '',

        allowedChars: /^[A-Za-z\sÅÄÖåäö\d=+\-_)(*&^%$#@![\]{}';:"/.,?><\\|]*$/,
        mentionDenotationChars: ['/', '#', '@'],
        spaceAfterInsert: false,
        maxChars: 300,
        renderItem: function (item: any) {
          // quill-mention only renders strings via innerText, so we must
          // return a DOM Node to control the markup.
          if (item?.denotationChar === '/' || item?.denotationChar === '#') {
            const row = document.createElement('span')
            row.className = 'ql-mention-item-row'

            const keyEl = document.createElement('span')
            keyEl.className = 'ql-mention-item-key'
            keyEl.textContent = `${item.denotationChar}${item.value ?? ''}`
            row.appendChild(keyEl)

            if (item.title) {
              const titleEl = document.createElement('span')
              titleEl.className = 'ql-mention-item-title'
              titleEl.textContent = String(item.title)
              row.appendChild(titleEl)
            }
            return row
          }
          return String(item?.value ?? '')
        },
        source: async function (searchTerm: any, renderList: any, mentionChar: any) {
          const stamp = (items: any[]) =>
            Array.isArray(items)
              ? items.map((it) => ({ ...it, denotationChar: mentionChar }))
              : []
          if (mentionChar === '/') {
            const matchedPeople = await suggestTask(searchTerm.slice(1));
            renderList(stamp(matchedPeople || []), searchTerm);
          } else if (mentionChar === '#') {
            const matchedPages = await suggestPages(searchTerm.slice(1));
            renderList(stamp(matchedPages || []), searchTerm);
          } else if (mentionChar === '@') {
            const matchedPeople = await suggestAlertOptions(searchTerm.slice(1));
            renderList(matchedPeople, searchTerm);

          } else {
            renderList([], searchTerm); // No valid character
          }
        },
        onSelect: function (item: any, insertItem: any) {
          insertItem(item); // Insert the mention
          if (item.denotationChar === '@') {
            setTimeout(() => {
              // Modify the inserted mention element
              const mentions = document.querySelectorAll(`[data-id="${item.id}"]`);
              mentions.forEach((mention) => {
                const vvid = uuidv4();
                mention.setAttribute('data-unique-id', vvid);
                mention.setAttribute('data-description', 'new');
              });
            }, 0); // Delay to ensure DOM update
          }
        },
      },
    }),

    []
  )

  // Snapshot the scroll position on toolbar mousedown, before Quill's change
  // handler runs quill.focus() (which scrolls the editor to the top). The
  // header handler restores from this snapshot after applying the format.
  useEffect(() => {
    const quill = quillRef.current?.getEditor()
    const toolbar = quill?.getModule('toolbar')?.container as HTMLElement | undefined
    if (!quill || !toolbar) return
    const snapshot = () => {
      toolbarScrollSnapshotRef.current = captureScrollPins(quill.root)
    }
    toolbar.addEventListener('mousedown', snapshot, true)
    return () => toolbar.removeEventListener('mousedown', snapshot, true)
  }, [])

  useEffect(() => {
    if (!quillRef.current) return;

    const quill = quillRef.current.getEditor();
    quill.root.addEventListener('keydown', handleKeyDown);

    return () => {
      quill.root.removeEventListener('keydown', handleKeyDown);
    };
  }, []);


  const handleKeyDown = (e: any) => {
    const quill = quillRef.current.getEditor();
    const range = quill.getSelection();
    if (e.key === 'Tab') {
      if (range) {
        // In a list, Tab indents the item — skip the format-clearing call,
        // which would strip the list format from the following line and
        // split the <ol>, breaking the numbering of subsequent items.
        const format = quill.getFormat(range.index);
        if (format.list) return;
        setTimeout(() => {
          quill.removeFormat(range.index + 1, 0);
        }, 0);
      }
    }
  };
  return (
    <>
      <div ref={editorContainerRef} className={`text-editor bg-white  ${isHome && 'home_editor'}  ${theme === 'dark' ? `dark-mode-placeholder` : ''}`} style={{ position: 'relative' }}>
        <ReactQuill
          theme='snow'
          value={editor ? editor : ''}
          onChange={handleChange}
          placeholder={placeholder}
          modules={modules}
          formats={formats}
          ref={quillRef}
          tabIndex={tabIndex}
          className={`image_resize_textEditor  ${theme === 'dark'
            ? (isHome ? 'layout__dark__div' : 'dark__theme__input')
            : (isHome ? 'text-dark white_editor__placeholder' : 'text-dark') 
            } ${isNewModal ? 'modal-editor' : ''}`}
          onBlur={onBlur}
          // readOnly={forTitle === 'description' && role?.isGuest === true}
          readOnly={role && role === 'Guest' ? true : false}
        />

        <TableInsertModal
          show={showTableModal}
          onClose={() => {
            setShowTableModal(false);
            savedCursorPosition.current = null;
          }}
          onInsert={handleTableInsert}
          theme={theme}
          editorContainerRef={editorContainerRef}
        />

        <TableEditControls quillRef={quillRef} />

        {/* Inline "+" checklist dropdown */}
        {enableChecklist && checklistDropdown && (
          <div
            className={`ql-checklist-plus-dropdown ${theme === 'dark' ? 'dark' : ''}`}
            style={{
              position: 'fixed',
              top: checklistDropdown.top,
              left: checklistDropdown.left,
              zIndex: 9001,
            }}
          >
            <button
              type="button"
              className="ql-checklist-plus-option"
              onMouseDown={(e) => {
                // Prevent blur of the editor before we insert
                e.preventDefault()
                const idx = checklistDropdown.plusIndex
                setChecklistDropdown(null)
                insertChecklistReplacingPlus(idx)
              }}
            >
              <span className="ql-checklist-plus-option-icon">+</span>
              <span className="ql-checklist-plus-option-label">Checklist</span>
            </button>
          </div>
        )}

        {/* Per-item Assigned To dropdown */}
        {enableChecklist && assigneePicker && (
          <ChecklistAssigneeDropdown
            anchorRect={assigneePicker.anchorRect}
            initialAssignees={assigneePicker.initial}
            boardMembers={checklistBoardMembers}
            onClose={handleAssigneePickerClose}
            onOpenCreateContact={(name: string) => {
              if (!assigneePicker?.item) return
              const itemId =
                assigneePicker.item.getAttribute('data-checklist-item-id') ?? ''
              setCreateContactModal({ name, itemId })
              setCreateContactFormValues({ fullName: name })
            }}
            theme={theme}
          />
        )}

        {/* Per-item due date+time picker */}
        {enableChecklist && reminderPicker && (
          <ChecklistDueDatePicker
            anchorRect={reminderPicker.anchorRect}
            initial={reminderPicker.initial}
            onSave={(date: Date) => {
              const item = reminderPicker.item
              writeChecklistDueDate(item, date)
              item.dispatchEvent(new CustomEvent('checklist-mutated', { bubbles: true }))
              // If this item is linked to a task, push the new due date
              // to the task so the task tile / modal reflect it too.
              const taskId = item.getAttribute('data-checklist-task-id')
              const taskKey = item.getAttribute('data-checklist-task-key')
              if (taskId) {
                const linkedCard =
                  allCurrentUserCardsRef.current?.find(
                    (c: any) =>
                      c?.id === taskId ||
                      (typeof c?.key === 'string' && taskKey && c.key.trim() === taskKey)
                  ) ||
                  boardScopedCardsRef.current?.find(
                    (c: any) =>
                      c?.id === taskId ||
                      (typeof c?.key === 'string' && taskKey && c.key.trim() === taskKey)
                  )
                if (linkedCard) {
                  const { due_date, due_time } = readChecklistDueDateRaw(item)
                  pushDueDateToTask(taskId, { due_date, due_time }, linkedCard)
                }
              }
              setReminderPicker(null)
            }}
            onClear={() => {
              const item = reminderPicker.item
              writeChecklistDueDate(item, null)
              item.dispatchEvent(new CustomEvent('checklist-mutated', { bubbles: true }))
              const taskId = item.getAttribute('data-checklist-task-id')
              const taskKey = item.getAttribute('data-checklist-task-key')
              if (taskId) {
                const linkedCard =
                  allCurrentUserCardsRef.current?.find(
                    (c: any) =>
                      c?.id === taskId ||
                      (typeof c?.key === 'string' && taskKey && c.key.trim() === taskKey)
                  ) ||
                  boardScopedCardsRef.current?.find(
                    (c: any) =>
                      c?.id === taskId ||
                      (typeof c?.key === 'string' && taskKey && c.key.trim() === taskKey)
                  )
                if (linkedCard) {
                  pushDueDateToTask(taskId, { due_date: null, due_time: null }, linkedCard)
                }
              }
              setReminderPicker(null)
            }}
            onCancel={() => setReminderPicker(null)}
            theme={theme}
          />
        )}

        {/* Add New Contact modal — opened from the dropdown's
            "+ Add <name> to contacts" row. On save, upgrades the freetext
            assignee to a proper contact entry so the alert fires. */}
        {enableChecklist && createContactModal && (
          <AddNewContactModal
            show
            handleClose={() => {
              setCreateContactModal(null)
              setCreateContactFormValues({})
            }}
            formValues={createContactFormValues}
            setFormValues={setCreateContactFormValues}
            getOtherContactHandler={async () => {
              await getOtherContactHandler()
              if (createContactModal) {
                await upgradeFreeTextToContact(
                  createContactModal.itemId,
                  createContactModal.name
                )
              }
              setCreateContactModal(null)
              setCreateContactFormValues({})
            }}
          />
        )}

        {/* Heading-trash confirm modal */}
        {enableChecklist && showChecklistDeleteConfirm && (
          <div
            className={`ql-checklist-confirm-overlay`}
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) cancelDeleteChecklist()
            }}
          >
            <div className={`ql-checklist-confirm-modal ${theme === 'dark' ? 'dark' : ''}`}>
              <div className="ql-checklist-confirm-title">Delete checklist?</div>
              <div className="ql-checklist-confirm-body">
                This will permanently delete the entire checklist and all its items.
              </div>
              <div className="ql-checklist-confirm-actions">
                <button
                  type="button"
                  className="ql-checklist-confirm-cancel"
                  onClick={cancelDeleteChecklist}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ql-checklist-confirm-delete"
                  onClick={confirmDeleteChecklist}
                >
                  Delete Checklist
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Per-item delete confirm — only shown when the item has a task
            created from it. Deleting the item does NOT delete that task. */}
        {enableChecklist && itemDeleteConfirm && (
          <div
            className="ql-checklist-confirm-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) cancelDeleteChecklistItem()
            }}
          >
            <div className={`ql-checklist-confirm-modal ${theme === 'dark' ? 'dark' : ''}`}>
              <div className="ql-checklist-confirm-title">Delete checklist item?</div>
              <div className="ql-checklist-confirm-body">
                You created task {itemDeleteConfirm.taskKey} from this item.
                Deleting the item will not delete that task — it will remain, and
                you&apos;ll need to find and delete it yourself if you no longer want it.
              </div>
              <div className="ql-checklist-confirm-actions">
                <button
                  type="button"
                  className="ql-checklist-confirm-cancel"
                  onClick={cancelDeleteChecklistItem}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="ql-checklist-confirm-delete"
                  onClick={confirmDeleteChecklistItem}
                >
                  Delete Item
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Create-task-from-checklist-item modal */}
        {enableChecklist && taskFromChecklist && (
          <ChecklistCreateTaskModal
            theme={theme}
            task={taskFromChecklist}
            saving={taskFromChecklistSaving}
            error={taskFromChecklistError}
            hasCreateHandler={!!onCreateTaskFromChecklistItem}
            showColumnField={Array.isArray(columns) && !!onCreateColumnFromChecklist}
            columns={columns ?? []}
            columnChoice={columnChoice}
            setColumnChoice={setColumnChoice}
            onTextChange={(text) =>
              setTaskFromChecklist((prev) => (prev ? { ...prev, text } : prev))
            }
            onClose={() => {
              setTaskFromChecklist(null)
              setTaskFromChecklistError(null)
            }}
            onConfirm={handleConfirmCreateTask}
          />
        )}

        {/* Task preview modal — shown when the T icon is clicked on an
            already-linked checklist item. Click-away closes; Go to this
            Task navigates the host modal to the linked card. */}
        {enableChecklist && taskPreview && (
          <div
            className="ql-checklist-confirm-overlay"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setTaskPreview(null)
            }}
          >
            <div className={`ql-checklist-confirm-modal ${theme === 'dark' ? 'dark' : ''}`}>
              <div className="ql-checklist-task-preview-id">{taskPreview.taskKey}</div>
              <div className="ql-checklist-task-preview-title">{taskPreview.title}</div>
              <div className="ql-checklist-confirm-actions">
                <button
                  type="button"
                  className="ql-checklist-task-preview-link"
                  onClick={() => {
                    const match = allCurrentUserCards?.find(
                      (c: any) =>
                        c?.id === taskPreview.taskId ||
                        (typeof c?.key === 'string' && c.key.trim() === taskPreview.taskKey)
                    )
                    if (!match) {
                      // Card hasn't hydrated into the store yet — bail silently;
                      // user can click again once the refetch completes.
                      setTaskPreview(null)
                      return
                    }
                    if (currentCardOrPage && currentCardOrPage.id) {
                      pushToHistory(currentCardOrPage)
                    }
                    if (handleFormSubmission) handleFormSubmission()
                    setExpandedFromSource(!!isExpandedModalRef.current)
                    setCard(match)
                    setShowEditModalQuill(true)
                    setTaskPreview(null)
                  }}
                >
                  Go to this Task
                </button>
              </div>
            </div>
          </div>
        )}

        {showEmojiPicker && (
          <div
            ref={emojiPickerRef}
            style={{
              position: 'absolute',
              top: '50px',
              right: '10px',
              zIndex: 1000,
            }}
          >
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              theme={theme === 'dark' ? Theme.DARK : Theme.LIGHT}
              width={350}
              height={400}
            />
          </div>
        )}

        {showTextColorPicker && (
          <div
            ref={textColorPickerRef}
            className={`highlight-picker ${theme === 'dark' ? 'dark' : ''}`}
          >
            <div className="highlight-picker__header">
              <span className="highlight-picker__title">
                Text Color
              </span>
              <input
                ref={textColorInputRef}
                type="color"
                value={textColor}
                onChange={(e) => applyTextColor(e.target.value, false)}
                className="highlight-picker__color-input"
                title="Custom color"
              />
              <button
                type="button"
                onClick={() => textColorInputRef.current?.click()}
                className="highlight-picker__brush-btn"
                title="More Colors"
              >
                <i className="bi bi-brush-fill"></i>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowTextColorPicker(false);
                  savedCursorPosition.current = null;
                }}
                className="highlight-picker__done-btn"
                title="Close"
              >
                Done
              </button>
            </div>
            <div className="highlight-picker__colors">
              {highlightPickerColors.map((color, index) => (
                <div
                  key={index}
                  onClick={() => applyTextColor(color)}
                  className={`highlight-picker__color-swatch ${textColor.toLowerCase() === color.toLowerCase() ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}

        {showHighlightPicker && (
          <div
            ref={highlightPickerRef}
            className={`highlight-picker ${theme === 'dark' ? 'dark' : ''}`}
          >
            <div className="highlight-picker__header">
              <span className="highlight-picker__title">
                Highlight Color
              </span>
              <input
                ref={colorInputRef}
                type="color"
                value={highlightColor}
                onChange={(e) => applyHighlightColor(e.target.value, false)}
                className="highlight-picker__color-input"
                title="Custom color"
              />
              <button
                type="button"
                onClick={() => colorInputRef.current?.click()}
                className="highlight-picker__brush-btn"
                title="More Colors"
              >
                <i className="bi bi-brush-fill"></i>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowHighlightPicker(false);
                  savedCursorPosition.current = null;
                }}
                className="highlight-picker__done-btn"
                title="Close"
              >
                Done
              </button>
            </div>
            <div className="highlight-picker__colors">
              {highlightPickerColors.map((color, index) => (
                <div
                  key={index}
                  onClick={() => applyHighlightColor(color)}
                  className={`highlight-picker__color-swatch ${highlightColor.toLowerCase() === color.toLowerCase() ? 'selected' : ''}`}
                  style={{ backgroundColor: color }}
                  title={color}
                />
              ))}
            </div>
          </div>
        )}

        {cardSelected && (
          <ReactToolTip id={`my-tooltip-children-multiline-write-${cardSelected?.key?.trim()}`}>
            <div id='react-tooltip-custom'>
              <div className='custom-card'>
                <h5 className={theme === 'dark' ? 'white__text' : ''} style={{ margin: 0, textDecoration: 'underline' }}>{cardSelected?.card_name}</h5>
                <span className='custom-card-description'>
                  {cardSelected?.description ? (
                    <CardData data={cardSelected?.description} />
                  ) : (
                    <CardData
                      data={`<span>
                    No Description
                  </span>`}
                    />
                  )}
                </span>
              </div>
            </div>
          </ReactToolTip>
        )}
        {pageSelected && (
          <ReactToolTip id={`my-tooltip-children-multiline-write-${pageSelected?.key?.trim()}`}>
            <div id='react-tooltip-custom'>
              <div className='custom-card'>
                <h5 className={theme === 'dark' ? 'white__text' : ''} style={{ margin: 0, textDecoration: 'underline' }}>{pageSelected?.title}</h5>
                <span className='custom-card-description'>
                  {pageSelected?.description ? (
                    <CardData data={pageSelected?.description} />
                  ) : (
                    <CardData
                      data={`<span>
                    No Description
                  </span>`}
                    />
                  )}
                </span>
              </div>
            </div>
          </ReactToolTip>
        )}
      </div>

      {isVideoSrcGet && (
        <p>
          If video is not preview or loaded properly! Kindly disable any ad blockers or
          privacy-related browser extensions temporarily
        </p>
      )}
      <ImageModal ref={editRef} />
    </>
  )
}

export const TextEditor = React.memo(MemoizedTextEditor)
